import { __extends } from "tslib";
import { assert } from '@awayjs/graphics';
import { release, notImplemented, defineNonEnumerableProperty, isIndex, isNullOrUndefined, isObject, } from '@awayfl/swf-loader';
import { Namespace } from '../abc/lazy/Namespace';
import { Multiname } from '../abc/lazy/Multiname';
import { internPrefixedNamespace } from '../abc/lazy/internPrefixedNamespace';
import { internNamespace } from '../abc/lazy/internNamespace';
import { ASObject } from '../nat/ASObject';
import { addPrototypeFunctionAlias } from '../nat/addPrototypeFunctionAlias';
import { Errors } from '../errors';
import { axCoerceString } from '../run/axCoerceString';
import { checkValue } from '../run/checkValue';
import { validateCall } from '../run/validateCall';
import { getCurrentScope } from '../run/getCurrentScope';
/* tslint:disable */
/*
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/*
 NOTE ON E4X METHOD CALLS

 E4X specifies some magic when making calls on XML and XMLList values. If a
 callee is not found on an XMLList value and the list has only one XML
 child, then the call is delegated to that XML child. If a callee is not
 found on an XML value and that value has simple content, then the simple
 content is converted to a string value and the call is made on that string
 value.

 Here are the relevant texts from the spec section 11.2.2.1:

 "If no such property exists and base is an XMLList of size 1, CallMethod
 delegates the method invocation to the single property it contains. This
 treatment intentionally blurs the distinction between XML objects and XMLLists
 of size 1."

 "If no such property exists and base is an XML object containing no XML valued
 children (i.e., an attribute, leaf node or element with simple content),
 CallMethod attempts to delegate the method lookup to the string value
 contained in the leaf node. This treatment allows users to perform operations
 directly on the value of a leaf node without having to explicitly select it."

 NOTE ON E4X ANY NAME AND NAMESPACE

 E4X allows the names of the form x.*, x.ns::*, x.*::id and x.*::* and their
 attribute name counterparts x.@*, x.@ns::*, etc. These forms result in
 Multiname values with the name part equal to undefined in the case of an ANY
 name, and the namespace set being empty in the case of an ANY namespace.

 Note also,
 x.*
 is shorthand for
 x.*::*
 .

 */
export function isXMLType(val, sec) {
    return typeof val === 'object' && val &&
        (val.axClass === sec.AXXML || val.axClass === sec.AXXMLList ||
            val.axClass === sec.AXQName || val.axClass === sec.AXNamespace);
}
export function isXMLCollection(sec, val) {
    return typeof val === 'object' && val &&
        (val.axClass === sec.AXXML || val.axClass === sec.AXXMLList);
}
// 10.1 ToString
function toString(node, sec) {
    if (!node || node.axClass !== sec.AXXML) {
        return axCoerceString(node);
    }
    switch (node._kind) {
        case 3 /* ASXMLKind.Text */:
        case 2 /* ASXMLKind.Attribute */:
            return node._value;
        default:
            if (node.hasSimpleContent()) {
                var s = '';
                for (var i = 0; i < node._children.length; i++) {
                    var child = node._children[i];
                    if (child._kind === 4 /* ASXMLKind.Comment */ ||
                        child._kind === 5 /* ASXMLKind.ProcessingInstruction */) {
                        continue;
                    }
                    s += toString(child, sec);
                }
                return s;
            }
            return toXMLString(sec, node);
    }
}
// 10.2.1.1 EscapeElementValue ( s )
export function escapeElementValue(sec, s) {
    if (isXMLCollection(sec, s)) {
        return s.toXMLString();
    }
    s = axCoerceString(s);
    var i = 0, ch;
    while (i < s.length && (ch = s[i]) !== '&' && ch !== '<' && ch !== '>') {
        i++;
    }
    if (i >= s.length) {
        return s;
    }
    var buf = s.substring(0, i);
    while (i < s.length) {
        ch = s[i++];
        switch (ch) {
            case '&':
                buf += '&amp;';
                break;
            case '<':
                buf += '&lt;';
                break;
            case '>':
                buf += '&gt;';
                break;
            default:
                buf += ch;
                break;
        }
    }
    return buf;
}
// 10.2.1.2 EscapeAttributeValue ( s )
export function escapeAttributeValue(s) {
    s = String(s);
    var i = 0, ch;
    while (i < s.length && (ch = s[i]) !== '&' && ch !== '<' &&
        ch !== '"' && ch !== '\n' && ch !== '\r' && ch !== '\t') {
        i++;
    }
    if (i >= s.length) {
        return s;
    }
    var buf = s.substring(0, i);
    while (i < s.length) {
        ch = s[i++];
        switch (ch) {
            case '&':
                buf += '&amp;';
                break;
            case '<':
                buf += '&lt;';
                break;
            case '"':
                buf += '&quot;';
                break;
            case '\n':
                buf += '&#xA;';
                break;
            case '\r':
                buf += '&#xD;';
                break;
            case '\t':
                buf += '&#x9;';
                break;
            default:
                buf += ch;
                break;
        }
    }
    return buf;
}
function isWhitespace(s, index) {
    var ch = s[index];
    return ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t';
}
function isWhitespaceString(s) {
    release || assert(typeof s === 'string');
    for (var i = 0; i < s.length; i++) {
        var ch = s[i];
        if (!(ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t')) {
            return false;
        }
    }
    return true;
}
function trimWhitespaces(s) {
    var i = 0;
    while (i < s.length && isWhitespace(s, i)) {
        i++;
    }
    if (i >= s.length) {
        return '';
    }
    var j = s.length - 1;
    while (isWhitespace(s, j)) {
        j--;
    }
    return i === 0 && j === s.length - 1 ? s : s.substring(i, j + 1);
}
var indentStringCache = [];
function getIndentString(indent) {
    if (indent > 0) {
        if (indentStringCache[indent] !== undefined) {
            return indentStringCache[indent];
        }
        var s = '';
        for (var i = 0; i < indent; i++) {
            s += ' ';
        }
        indentStringCache[indent] = s;
        return s;
    }
    return '';
}
function generateUniquePrefix(namespaces) {
    var i = 1, newPrefix;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        newPrefix = '_ns' + i;
        if (!namespaces.some(function (ns) { return ns.prefix === newPrefix; })) {
            return newPrefix;
        }
        i++;
    }
}
// 10.2 ToXMLString
function toXMLString(sec, node) {
    if (node === null || node === undefined) {
        throw new TypeError();
    }
    return escapeElementValue(sec, node);
}
// 10.3 ToXML
function toXML(v, sec) {
    if (v === null) {
        sec.throwError('TypeError', Errors.ConvertNullToObjectError);
    }
    if (v === undefined) {
        sec.throwError('TypeError', Errors.ConvertUndefinedToObjectError);
    }
    if (v.axClass === sec.AXXML) {
        return v;
    }
    if (v.axClass === sec.AXXMLList) {
        if (v._children.length !== 1) {
            sec.throwError('TypeError', Errors.XMLMarkupMustBeWellFormed);
        }
        return v._children[0];
    }
    // The E4X spec says we must throw a TypeError for non-Boolean, Number, or String objects.
    // Flash thinks otherwise.
    var x = sec.xmlParser.parseFromString(axCoerceString(v));
    var length = x._children.length;
    if (length === 0) {
        return createXML(sec, 3 /* ASXMLKind.Text */);
    }
    if (length === 1) {
        x._children[0]._parent = null;
        return x._children[0];
    }
    if (length === 2) {
        x._children[1]._parent = null;
        return x._children[1];
    }
    sec.throwError('TypeError', Errors.XMLMarkupMustBeWellFormed);
}
// 10.4 ToXMLList
function toXMLList(value, targetList) {
    // toXMLList is supposed to just return value if it's an XMLList already. For optimization
    // purposes, we handle that case at the callsites.
    release || assert(typeof value !== 'object' || value && value.axClass !== targetList.axClass);
    if (value === null) {
        targetList.sec.throwError('TypeError', Errors.ConvertNullToObjectError);
    }
    if (value === undefined) {
        targetList.sec.throwError('TypeError', Errors.ConvertUndefinedToObjectError);
    }
    if (value.axClass === targetList.sec.AXXML) {
        targetList.append(value);
        return;
    }
    // The E4X spec says we must throw a TypeError for non-Boolean, Number, or String objects.
    // Flash thinks otherwise.
    var defaultNamespace = getDefaultNamespace(targetList.sec);
    var parentString = '<parent xmlns="' + escapeAttributeValue(defaultNamespace.uri) + '">' +
        value + '</parent>';
    var x = toXML(parentString, targetList.sec);
    var children = x._children;
    if (!children) {
        return;
    }
    for (var i = 0; i < children.length; i++) {
        var v = children[i];
        v._parent = null;
        targetList.append(v);
    }
}
// 10.6 ToXMLName
function toXMLName(mn, sec) {
    if (mn === undefined) {
        return anyMultiname;
    }
    var name;
    // convert argument to a value of type AttributeName or a QName object
    // according to the following:
    if (typeof mn === 'object' && mn !== null) {
        if (mn instanceof Multiname) {
            return mn;
        }
        if (mn.axClass === sec.AXQName) {
            // Object - If the input argument is a QName object,
            // return its Multiname.
            return mn.name;
        }
        // Object - Otherwise, convert the input argument to a string using ToString.
        name = String(mn);
    }
    else if (typeof mn === 'number') {
        name = mn + '';
    }
    else if (typeof mn === 'string') {
        // String - Create a QName object or AttributeName from the String
        // as specified below in section 10.6.1. See below.
        if (mn === '*') {
            name = null;
        }
        else {
            name = mn;
        }
    }
    else {
        sec.throwError('TypeError', Errors.XMLInvalidName, mn);
    }
    // ... then convert the result to a QName object or AttributeName
    // as specified in section 10.6.1.
    if (name && name[0] === '@') {
        // If the first character of s is "@", ToXMLName creates an
        // AttributeName using the ToAttributeName operator.
        name = name.substr(1);
        if (name === '*') {
            name = null;
        }
        return new Multiname(null, 0, 13 /* CONSTANT.QNameA */, [Namespace.PUBLIC], name);
    }
    return new Multiname(null, 0, 7 /* CONSTANT.QName */, [Namespace.PUBLIC], name);
}
function coerceE4XMultiname(mn, sec) {
    var out = tmpMultiname;
    out.kind = mn.kind;
    // Queries of the foo[new QName('bar')] sort create this situation.
    if (mn.name && mn.name.axClass === sec.AXQName) {
        mn = mn.name.name;
    }
    if (mn.isQName()) {
        out.name = mn.name;
        out.namespaces = mn.namespaces;
    }
    else {
        if (mn.isAnyNamespace()) {
            out.namespaces = mn.namespaces;
        }
        else {
            var defaultNS = getDefaultNamespace(sec);
            var namespaces = mn.namespaces;
            var containsDefaultNS = false;
            for (var i = 0; i < namespaces.length; i++) {
                var ns = namespaces[i];
                if (ns.uri === defaultNS.uri && ns.prefix === defaultNS.prefix &&
                    ns.type === defaultNS.type) {
                    containsDefaultNS = true;
                    break;
                }
            }
            if (!containsDefaultNS) {
                out.namespaces = mn.namespaces.concat(defaultNS);
            }
            else {
                out.namespaces = mn.namespaces;
            }
        }
    }
    var name = mn.name;
    if (mn.isAnyName() || name === '*' || name === null) {
        out.name = null;
    }
    else if (name.length > 1 && name[0] === '@') {
        if (!out.isAttribute()) {
            if (name === '@*') {
                out.name = null;
            }
            else {
                out.name = name.substr(1);
            }
            out.kind = out.namespaces.length === 1 ? 13 /* CONSTANT.QNameA */ : 14 /* CONSTANT.MultinameA */;
        }
        else {
            out.name = name;
        }
    }
    else {
        out.name = name;
    }
    return out;
}
// 12.1 GetDefaultNamespace
function getDefaultNamespace(sec) {
    var scope = getCurrentScope();
    while (scope) {
        if (scope.defaultNamespace) {
            return scope.defaultNamespace;
        }
        scope = scope.parent;
    }
    if (!sec.AXNamespace.defaultNamespace) {
        sec.AXNamespace.defaultNamespace = new Namespace(0 /* NamespaceType.Public */, 'default', '');
    }
    // The outermost default xml namespace is stored in sec.AXNamespace.defaultNamespace.
    return sec.AXNamespace.defaultNamespace;
}
/**
 * 13.3.5.4 [[GetNamespace]] ( [ InScopeNamespaces ] )
 *
 * The [[GetNamespace]] method is an internal method that returns a Namespace object with a URI
 * matching the URI of this QName. InScopeNamespaces is an optional parameter. If
 * InScopeNamespaces is unspecified, it is set to the empty set. If one or more Namespaces
 * exists in InScopeNamespaces with a URI matching the URI of this QName, one of the matching
 * Namespaces will be returned. If no such namespace exists in InScopeNamespaces,
 * [[GetNamespace]] creates and returns a new Namespace with a URI matching that of this QName.
 * For implementations that preserve prefixes in QNames, [[GetNamespace]] may return a
 * Namespace that also has a matching prefix. The input argument InScopeNamespaces is a set of
 * Namespace objects.
 */
function GetNamespace(mn, inScopeNamespaces) {
    release || assert(mn.isQName());
    var uri = mn.uri;
    for (var i = 0; inScopeNamespaces && i < inScopeNamespaces.length; i++) {
        if (uri === inScopeNamespaces[i].uri) {
            return inScopeNamespaces[i];
        }
    }
    return mn.namespaces[0];
}
// 13.1.2.1 isXMLName ( value )
export function isXMLName(v, sec) {
    try {
        sec.AXQName.Create(v);
    }
    catch (e) {
        return false;
    }
    // FIXME scan v to see if it is a valid lexeme and return false if not
    return true;
}
var tmpMultiname = new Multiname(null, 0, 7 /* CONSTANT.QName */, [], null, null, true);
var anyMultiname = new Multiname(null, 0, 7 /* CONSTANT.QName */, [], null, null, true);
release || Object.seal(anyMultiname);
var XMLParserBase = /** @class */ (function () {
    function XMLParserBase() {
    }
    XMLParserBase.prototype.resolveEntities = function (s) {
        return s.replace(/&([^;]+);/g, function (all, entity) {
            if (entity.substring(0, 2) === '#x') {
                return String.fromCharCode(parseInt(entity.substring(2), 16));
            }
            else if (entity.substring(0, 1) === '#') {
                return String.fromCharCode(parseInt(entity.substring(1), 10));
            }
            switch (entity) {
                case 'lt':
                    return '<';
                case 'gt':
                    return '>';
                case 'amp':
                    return '&';
                case 'quot':
                    return '"';
            }
            // throw "Unknown entity: " + entity;
            return all;
        });
    };
    XMLParserBase.prototype.parseContent = function (s, start) {
        var pos = start;
        var attributes = [];
        function skipWs() {
            while (pos < s.length && isWhitespace(s, pos)) {
                ++pos;
            }
        }
        while (pos < s.length && !isWhitespace(s, pos) && s[pos] !== '>' && s[pos] !== '/') {
            ++pos;
        }
        var name = s.substring(start, pos);
        skipWs();
        while (pos < s.length &&
            s[pos] !== '>' &&
            s[pos] !== '/' &&
            s[pos] !== '?') {
            skipWs();
            var attrName = '', attrValue = '';
            while (pos < s.length && !isWhitespace(s, pos) && s[pos] !== '=') {
                attrName += s[pos];
                ++pos;
            }
            skipWs();
            if (s[pos] !== '=') {
                return null;
            }
            ++pos;
            skipWs();
            var attrEndChar = s[pos];
            if (attrEndChar !== '"' && attrEndChar !== '\'') {
                return null;
            }
            var attrEndIndex = s.indexOf(attrEndChar, ++pos);
            if (attrEndIndex < 0) {
                return null;
            }
            attrValue = s.substring(pos, attrEndIndex);
            attributes.push({ name: attrName, value: this.resolveEntities(attrValue) });
            pos = attrEndIndex + 1;
            skipWs();
        }
        return { name: name, attributes: attributes, parsed: pos - start };
    };
    XMLParserBase.prototype.parseProcessingInstruction = function (s, start) {
        var pos = start;
        function skipWs() {
            while (pos < s.length && isWhitespace(s, pos)) {
                ++pos;
            }
        }
        while (pos < s.length && !isWhitespace(s, pos) && s[pos] !== '>' && s[pos] !== '/') {
            ++pos;
        }
        var name = s.substring(start, pos);
        skipWs();
        var attrStart = pos;
        while (pos < s.length && (s[pos] !== '?' || s[pos + 1] != '>')) {
            ++pos;
        }
        var value = s.substring(attrStart, pos);
        return { name: name, value: value, parsed: pos - start };
    };
    XMLParserBase.prototype.parseXml = function (s) {
        var i = 0;
        while (i < s.length) {
            var ch = s[i];
            var j = i;
            if (ch === '<') {
                ++j;
                var ch2 = s[j];
                var q = void 0;
                switch (ch2) {
                    case '/':
                        ++j;
                        q = s.indexOf('>', j);
                        if (q < 0) {
                            this.onError(-9 /* XMLParserErrorCode.UnterminatedElement */);
                            return;
                        }
                        this.onEndElement(s.substring(j, q));
                        j = q + 1;
                        break;
                    case '?': {
                        ++j;
                        var pi = this.parseProcessingInstruction(s, j);
                        if (s.substring(j + pi.parsed, j + pi.parsed + 2) != '?>') {
                            this.onError(-3 /* XMLParserErrorCode.UnterminatedXmlDeclaration */);
                            return;
                        }
                        this.onPi(pi.name, pi.value);
                        j += pi.parsed + 2;
                        break;
                    }
                    case '!':
                        if (s.substring(j + 1, j + 3) === '--') {
                            q = s.indexOf('-->', j + 3);
                            if (q < 0) {
                                this.onError(-5 /* XMLParserErrorCode.UnterminatedComment */);
                                return;
                            }
                            this.onComment(s.substring(j + 3, q));
                            j = q + 3;
                        }
                        else if (s.substring(j + 1, j + 8) === '[CDATA[') {
                            q = s.indexOf(']]>', j + 8);
                            if (q < 0) {
                                this.onError(-2 /* XMLParserErrorCode.UnterminatedCdat */);
                                return;
                            }
                            this.onCdata(s.substring(j + 8, q));
                            j = q + 3;
                        }
                        else if (s.substring(j + 1, j + 8) === 'DOCTYPE') {
                            var q2 = s.indexOf('[', j + 8);
                            var complexDoctype = false;
                            q = s.indexOf('>', j + 8);
                            if (q < 0) {
                                this.onError(-4 /* XMLParserErrorCode.UnterminatedDoctypeDeclaration */);
                                return;
                            }
                            if (q2 > 0 && q > q2) {
                                q = s.indexOf(']>', j + 8);
                                if (q < 0) {
                                    this.onError(-4 /* XMLParserErrorCode.UnterminatedDoctypeDeclaration */);
                                    return;
                                }
                                complexDoctype = true;
                            }
                            var doctypeContent = s.substring(j + 8, q + (complexDoctype ? 1 : 0));
                            this.onDoctype(doctypeContent);
                            // XXX pull entities ?
                            j = q + (complexDoctype ? 2 : 1);
                        }
                        else {
                            this.onError(-6 /* XMLParserErrorCode.MalformedElement */);
                            return;
                        }
                        break;
                    default: {
                        var content = this.parseContent(s, j);
                        if (content === null) {
                            this.onError(-6 /* XMLParserErrorCode.MalformedElement */);
                            return;
                        }
                        var isClosed = false;
                        if (s.substring(j + content.parsed, j + content.parsed + 2) === '/>') {
                            isClosed = true;
                        }
                        else if (s.substring(j + content.parsed, j + content.parsed + 1) !== '>') {
                            this.onError(-9 /* XMLParserErrorCode.UnterminatedElement */);
                            return;
                        }
                        this.onBeginElement(content.name, content.attributes, isClosed);
                        j += content.parsed + (isClosed ? 2 : 1);
                        break;
                    }
                }
            }
            else {
                do {
                    //
                } while (j++ < s.length && s[j] !== '<');
                var text = s.substring(i, j);
                this.onText(this.resolveEntities(text));
            }
            i = j;
        }
    };
    XMLParserBase.prototype.onPi = function (_name, _value) {
    };
    XMLParserBase.prototype.onComment = function (_text) {
    };
    XMLParserBase.prototype.onCdata = function (_text) {
    };
    XMLParserBase.prototype.onDoctype = function (_doctypeContent) {
    };
    XMLParserBase.prototype.onText = function (_text) {
    };
    XMLParserBase.prototype.onBeginElement = function (_name, _attributes, _isEmpty) {
    };
    XMLParserBase.prototype.onEndElement = function (_name) {
    };
    XMLParserBase.prototype.onError = function (_code) {
    };
    return XMLParserBase;
}());
export { XMLParserBase };
var XMLParser = /** @class */ (function (_super) {
    __extends(XMLParser, _super);
    function XMLParser(sec) {
        var _this = _super.call(this) || this;
        _this.sec = sec;
        _this.scopes = [];
        return _this;
    }
    XMLParser.prototype.isWhitespacePreserved = function () {
        var scopes = this.scopes;
        for (var j = scopes.length - 1; j >= 0; --j) {
            if (scopes[j].space === 'preserve') {
                return true;
            }
        }
        return false;
    };
    XMLParser.prototype.lookupDefaultNs = function () {
        var scopes = this.scopes;
        for (var j = scopes.length - 1; j >= 0; --j) {
            if ('xmlns' in scopes[j]) {
                return scopes[j].xmlns;
            }
        }
        return '';
    };
    XMLParser.prototype.lookupNs = function (prefix) {
        var scopes = this.scopes;
        for (var j = scopes.length - 1; j >= 0; --j) {
            if (prefix in scopes[j].lookup) {
                return scopes[j].lookup[prefix];
            }
        }
        return undefined;
    };
    XMLParser.prototype.getName = function (name, resolveDefaultNs) {
        var j = name.indexOf(':');
        if (j >= 0) {
            var prefix = name.substring(0, j);
            var localName = name.substring(j + 1);
            var namespace = this.lookupNs(prefix);
            if (namespace === undefined) {
                this.sec.throwError('TypeError', Errors.XMLPrefixNotBound, prefix, localName);
            }
            return {
                name: namespace + '::' + localName,
                localName: localName,
                prefix: prefix,
                namespace: namespace,
            };
        }
        else if (resolveDefaultNs) {
            return {
                name: name,
                localName: name,
                prefix: '',
                namespace: this.lookupDefaultNs()
            };
        }
        else {
            return {
                name: name,
                localName: name,
                prefix: '',
                namespace: ''
            };
        }
    };
    XMLParser.prototype.onError = function (code) {
        switch (code) {
            case -6 /* XMLParserErrorCode.MalformedElement */:
                this.sec.throwError('TypeError', Errors.XMLMalformedElement);
                return;
            case -9 /* XMLParserErrorCode.UnterminatedElement */:
                this.sec.throwError('TypeError', Errors.XMLUnterminatedElement);
                return;
            case -4 /* XMLParserErrorCode.UnterminatedDoctypeDeclaration */:
                this.sec.throwError('TypeError', Errors.XMLUnterminatedDocTypeDecl);
                return;
            case -2 /* XMLParserErrorCode.UnterminatedCdat */:
                this.sec.throwError('TypeError', Errors.XMLUnterminatedCData);
                return;
            case -5 /* XMLParserErrorCode.UnterminatedComment */:
                this.sec.throwError('TypeError', Errors.XMLUnterminatedComment);
                return;
            case -3 /* XMLParserErrorCode.UnterminatedXmlDeclaration */:
                this.sec.throwError('TypeError', Errors.XMLUnterminatedXMLDecl);
                return;
        }
    };
    XMLParser.prototype.onPi = function (name, value) {
        this.pi(name, value);
    };
    XMLParser.prototype.onComment = function (text) {
        this.comment(text);
    };
    XMLParser.prototype.onCdata = function (text) {
        this.cdata(text);
    };
    XMLParser.prototype.onDoctype = function (doctypeContent) {
        this.doctype(doctypeContent);
    };
    XMLParser.prototype.onText = function (text) {
        this.text(text, this.isWhitespacePreserved());
    };
    XMLParser.prototype.onBeginElement = function (name, contentAttributes, isEmpty) {
        var scopes = this.scopes;
        var scope = {
            namespaces: [],
            lookup: Object.create(null),
            inScopes: null
        };
        for (var q = 0; q < contentAttributes.length; ++q) {
            var attribute = contentAttributes[q];
            var attributeName = attribute.name;
            if (attributeName.substring(0, 6) === 'xmlns:') {
                var prefix = attributeName.substring(6);
                var uri = attribute.value;
                if (this.lookupNs(prefix) !== uri) {
                    scope.lookup[prefix] = trimWhitespaces(uri);
                    var ns = internPrefixedNamespace(0 /* NamespaceType.Public */, uri, prefix);
                    scope.namespaces.push(ns);
                }
                contentAttributes[q] = null;
            }
            else if (attributeName === 'xmlns') {
                var uri = attribute.value;
                if (this.lookupDefaultNs() !== uri) {
                    scope['xmlns'] = trimWhitespaces(uri);
                    var ns = internNamespace(0 /* NamespaceType.Public */, uri);
                    scope.namespaces.push(ns);
                }
                contentAttributes[q] = null;
            }
            else if (attributeName.substring(0, 4) === 'xml:') {
                var xmlAttrName = attributeName.substring(4);
                scope[xmlAttrName] = trimWhitespaces(attribute.value);
            }
            else {
                // skip ordinary attributes until all xmlns have been handled
            }
        }
        // build list of all namespaces including ancestors'
        var inScopeNamespaces = [];
        scope.namespaces.forEach(function (ns) {
            if (!ns.prefix || scope.lookup[ns.prefix] === ns.uri) {
                inScopeNamespaces.push(ns);
            }
        });
        scopes[scopes.length - 1].inScopes.forEach(function (ns) {
            if ((ns.prefix && !(ns.prefix in scope.lookup)) ||
                (!ns.prefix && !('xmlns' in scope))) {
                inScopeNamespaces.push(ns);
            }
        });
        scope.inScopes = inScopeNamespaces;
        scopes.push(scope);
        var attributes = [];
        for (var q = 0; q < contentAttributes.length; ++q) {
            var attribute = contentAttributes[q];
            if (attribute) {
                attributes.push({
                    name: this.getName(attribute.name, false),
                    value: attribute.value
                });
            }
        }
        this.beginElement(this.getName(name, true), attributes, inScopeNamespaces, isEmpty);
        if (isEmpty) {
            scopes.pop();
        }
    };
    XMLParser.prototype.onEndElement = function (name) {
        this.endElement(this.getName(name, true));
        this.scopes.pop();
    };
    XMLParser.prototype.beginElement = function (name, attrs, namespaces, isEmpty) {
        var parent = this.currentElement;
        this.elementsStack.push(parent);
        this.currentElement = createXML(this.sec, 1 /* ASXMLKind.Element */, name.namespace, name.localName, name.prefix);
        for (var i = 0; i < attrs.length; ++i) {
            var rawAttr = attrs[i];
            var attr = createXML(this.sec, 2 /* ASXMLKind.Attribute */, rawAttr.name.namespace, rawAttr.name.localName, rawAttr.name.prefix);
            attr._value = rawAttr.value;
            attr._parent = this.currentElement;
            this.currentElement._attributes.push(attr);
        }
        for (var i = 0; i < namespaces.length; ++i) {
            this.currentElement._inScopeNamespaces.push(namespaces[i]);
        }
        parent.insert(parent._children.length, this.currentElement);
        if (isEmpty) {
            this.currentElement = this.elementsStack.pop();
        }
    };
    XMLParser.prototype.endElement = function (_name) {
        this.currentElement = this.elementsStack.pop();
    };
    XMLParser.prototype.text = function (text, isWhitespacePreserve) {
        if (this.sec.AXXML.ignoreWhitespace) {
            text = trimWhitespaces(text);
        }
        // TODO: do an in-depth analysis of what isWhitespacePreserve is about.
        if (text.length === 0 || isWhitespacePreserve && this.sec.AXXML.ignoreWhitespace) {
            return;
        }
        var node = createXML(this.sec);
        node._value = text;
        this.currentElement.insert(this.currentElement._children.length, node);
    };
    XMLParser.prototype.cdata = function (text) {
        var node = createXML(this.sec);
        node._value = text;
        this.currentElement.insert(this.currentElement._children.length, node);
    };
    XMLParser.prototype.comment = function (text) {
        if (this.sec.AXXML.ignoreComments) {
            return;
        }
        var node = createXML(this.sec, 4 /* ASXMLKind.Comment */, '', '');
        node._value = text;
        this.currentElement.insert(this.currentElement._children.length, node);
    };
    XMLParser.prototype.pi = function (name, value) {
        if (this.sec.AXXML.ignoreProcessingInstructions) {
            return;
        }
        var node = createXML(this.sec, 5 /* ASXMLKind.ProcessingInstruction */, '', name);
        node._value = value;
        this.currentElement.insert(this.currentElement._children.length, node);
    };
    XMLParser.prototype.doctype = function (_text) { };
    XMLParser.prototype.parseFromString = function (s, _mimeType) {
        // placeholder
        var currentElement = this.currentElement = createXML(this.sec, 1 /* ASXMLKind.Element */, '', '', '');
        this.elementsStack = [];
        var defaultNs = getDefaultNamespace(this.sec);
        var scopes = [{
                namespaces: [],
                lookup: {
                    'xmlns': 'http://www.w3.org/2000/xmlns/',
                    'xml': 'http://www.w3.org/XML/1998/namespace'
                },
                inScopes: [defaultNs],
                space: 'default',
                xmlns: defaultNs.uri
            }];
        this.scopes = scopes;
        this.parseXml(s);
        this.currentElement = null;
        if (this.elementsStack.length > 0) {
            var nm = this.elementsStack.pop()._name.name;
            this.sec.throwError('TypeError', Errors.XMLUnterminatedElementTag, nm, nm);
        }
        this.elementsStack = null;
        return currentElement;
    };
    return XMLParser;
}(XMLParserBase));
export { XMLParser };
var ASNamespace = /** @class */ (function (_super) {
    __extends(ASNamespace, _super);
    /**
   * 13.2.2 The Namespace Constructor
   *
   * Namespace ()
   * Namespace (uriValue)
   * Namespace (prefixValue, uriValue)
   */
    function ASNamespace(uriOrPrefix_, uri_) {
        var _this = _super.call(this) || this;
        // 1. Create a new Namespace object n
        var uri = '';
        var prefix = '';
        // 2. If prefixValue is not specified and uriValue is not specified
        /*
        if (arguments.length === 0) {
            // a. Let n.prefix be the empty string
            // b. Let n.uri be the empty string
        }
        // 3. Else if prefixValue is not specified
        else
        */
        if (arguments.length === 1) {
            var uriValue = uriOrPrefix_;
            if (uriValue instanceof Namespace) {
                _this._ns = uriValue;
                return _this;
            }
            release || checkValue(uriValue);
            if (uriValue && typeof uriValue === 'object') {
                // Non-spec'ed, but very useful:
                // a. If Type(uriValue) is Object and uriValue.[[Class]] == "Namespace"
                if (uriValue.axClass === _this.sec.AXNamespace) {
                    var uriValueAsNamespace = uriValue;
                    // i. Let n.prefix = uriValue.prefix
                    prefix = uriValueAsNamespace.prefix;
                    uri = uriValueAsNamespace.uri;
                    // b. Else if Type(uriValue) is Object and uriValue.[[Class]] == "QName" and uriValue.uri
                    // is not null
                }
                else if (uriValue.axClass === _this.sec.AXQName &&
                    uriValue.uri !== null) {
                    // i. Let n.uri = uriValue.uri
                    uri = uriValue.uri;
                    // NOTE implementations that preserve prefixes in qualified names may also set n.prefix
                    // = uriValue.[[Prefix]]
                }
            }
            else { // c. Else
                // i. Let n.uri = ToString(uriValue)
                uri = toString(uriValue, _this.sec);
                // ii. If (n.uri is the empty string), let n.prefix be the empty string
                if (uri === '') {
                    prefix = '';
                }
                else { // iii. Else n.prefix = undefined
                    prefix = undefined;
                }
            }
        }
        else { // 4. Else
            var prefixValue = uriOrPrefix_;
            var uriValue = uri_;
            // a. If Type(uriValue) is Object and uriValue.[[Class]] == "QName" and uriValue.uri is not
            // null
            if (isObject(uriValue) &&
                uriValue.axClass === _this.sec.AXQName &&
                uriValue.uri !== null) {
                // i. Let n.uri = uriValue.uri
                uri = uriValue.uri;
            }
            else { // b. Else
                // i. Let n.uri = ToString(uriValue)
                uri = toString(uriValue, _this.sec);
            }
            // c. If n.uri is the empty string
            if (uri === '') {
                // i. If prefixValue is undefined or ToString(prefixValue) is the empty string
                if (prefixValue === undefined || toString(prefixValue, _this.sec) === '') {
                    // 1. Let n.prefix be the empty string
                    prefix = '';
                }
                else {
                    // ii. Else throw a TypeError exception
                    _this.sec.throwError('TypeError', Errors.XMLNamespaceWithPrefixAndNoURI, prefixValue);
                }
                // d. Else if prefixValue is undefined, let n.prefix = undefined
            }
            else if (prefixValue === undefined) {
                prefix = undefined;
                // e. Else if isXMLName(prefixValue) == false
            }
            else if (isXMLName(prefixValue, _this.sec) === false) {
                // i. Let n.prefix = undefined
                prefix = undefined;
                // f. Else let n.prefix = ToString(prefixValue)
            }
            else {
                prefix = toString(prefixValue, _this.sec);
            }
        }
        // 5. Return n
        _this._ns = internPrefixedNamespace(0 /* NamespaceType.Public */, uri, prefix);
        return _this;
    }
    ASNamespace.classInitializer = function () {
        defineNonEnumerableProperty(this, '$Bglength', 2);
        var proto = this.dPrototype;
        var asProto = ASNamespace.prototype;
        defineNonEnumerableProperty(proto, '$BgtoString', asProto.toString);
    };
    /**
   * 13.2.1 The Namespace Constructor Called as a Function
   *
   * Namespace ()
   * Namespace (uriValue)
   * Namespace (prefixValue, uriValue)
   */
    ASNamespace.axApply = function (_self, args) {
        var a = args[0];
        var b = args[1];
        // 1. If (prefixValue is not specified and Type(uriValue) is Object and
        // uriValue.[[Class]] == "Namespace")
        if (args.length === 1 && isObject(a) && a.axClass === this.sec.AXNamespace) {
            // a. Return uriValue
            return a;
        }
        // 2. Create and return a new Namespace object exactly as if the Namespace constructor had
        // been called with the same arguments (section 13.2.2).
        switch (args.length) {
            case 0:
                return this.sec.AXNamespace.Create();
            case 1:
                return this.sec.AXNamespace.Create(a);
            default:
                return this.sec.AXNamespace.Create(a, b);
        }
    };
    ASNamespace.Create = function (_uriOrPrefix_, _uri_) {
        var ns = Object.create(this.sec.AXNamespace.tPrototype);
        // The initializer relies on arguments.length being correct.
        // eslint-disable-next-line prefer-spread, prefer-rest-params
        ns.axInitializer.apply(ns, arguments);
        return ns;
    };
    ASNamespace.FromNamespace = function (ns) {
        var result = Object.create(this.sec.AXNamespace.tPrototype);
        result._ns = ns;
        return result;
    };
    // E4X 11.5.1 The Abstract Equality Comparison Algorithm, step 3.c.
    ASNamespace.prototype.equals = function (other) {
        return other && other.axClass === this.axClass &&
            other._ns.uri === this._ns.uri ||
            typeof other === 'string' && this._ns.uri === other;
    };
    Object.defineProperty(ASNamespace.prototype, "prefix", {
        get: function () {
            return this._ns.prefix;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASNamespace.prototype, "uri", {
        get: function () {
            return this._ns.uri;
        },
        enumerable: false,
        configurable: true
    });
    ASNamespace.prototype.toString = function () {
        if (this === this.axClass.dPrototype) {
            return '';
        }
        return this._ns.uri;
    };
    ASNamespace.prototype.valueOf = function () {
        if (this === this.axClass.dPrototype) {
            return '';
        }
        return this._ns.uri;
    };
    ASNamespace.instanceConstructor = ASNamespace;
    ASNamespace.defaultNamespace = Namespace.PUBLIC;
    return ASNamespace;
}(ASObject));
export { ASNamespace };
var ASQName = /** @class */ (function (_super) {
    __extends(ASQName, _super);
    /**
   * 13.3.2 The QName Constructor
   *
   * new QName ()
   * new QName (Name)
   * new QName (Namespace, Name)
   */
    function ASQName(nameOrNS_, name_) {
        var _this = _super.call(this) || this;
        var name;
        var namespace;
        if (arguments.length === 0) {
            name = '';
        }
        else if (arguments.length === 1) {
            name = nameOrNS_;
        }
        else { // if (arguments.length === 2) {
            namespace = nameOrNS_;
            name = name_;
        }
        // 1. If (Type(Name) is Object and Name.[[Class]] == "QName")
        if (name && name.axClass === _this.sec.AXQName) {
            // a. If (Namespace is not specified), return a copy of Name
            if (arguments.length < 2) {
                release || assert(name !== tmpMultiname);
                _this.name = name.name;
                return _this;
                // b. Else let Name = Name.localName
            }
            else {
                name = name.localName;
            }
        }
        // 2. If (Name is undefined or not specified)
        if (name === undefined) {
            // a. Let Name = ""
            name = '';
            // 3. Else let Name = ToString(Name)
        }
        else {
            name = toString(name, _this.sec);
        }
        // 4. If (Namespace is undefined or not specified)
        if (namespace === undefined) {
            // a. If Name = "*"
            if (name === '*') {
                // i. Let Namespace = null
                namespace = null;
            }
            else { // b. Else
                // i. Let Namespace = GetDefaultNamespace()
                namespace = getDefaultNamespace(_this.sec);
            }
        }
        // 5. Let q be a new QName with q.localName = Name
        var localName = name;
        var ns = null;
        // 6. If Namespace == null
        if (namespace !== null) {
            // a. Let Namespace be a new Namespace created as if by calling the constructor new
            // Namespace(Namespace)
            if (namespace.axClass !== _this.sec.AXNamespace) {
                namespace = _this.sec.AXNamespace.Create(namespace);
            }
            ns = namespace._ns;
            //// b. Let q.uri = Namespace.uri
            //uri = namespace.uri;
            // NOTE implementations that preserve prefixes in qualified names may also set
            // q.[[Prefix]] to Namespace.prefix
            //} else {
            // a. Let q.uri = null
            // NOTE implementations that preserve prefixes in qualified names may also set q.[[Prefix]]
            // to undefined
            //uri = null;
        }
        // 8. Return q
        _this.name = new Multiname(null, 0, 7 /* CONSTANT.QName */, [ns], localName);
        return _this;
    }
    ASQName.classInitializer = function () {
        defineNonEnumerableProperty(this, '$Bglength', 2);
        var proto = this.dPrototype;
        var asProto = ASQName.prototype;
        defineNonEnumerableProperty(proto, '$BgtoString', asProto.ecmaToString);
    };
    ASQName.Create = function (_nameOrNS_, _name_, _isAttribute) {
        var name = Object.create(this.sec.AXQName.tPrototype);
        // The initializer relies on arguments.length being correct.
        // eslint-disable-next-line prefer-spread, prefer-rest-params
        name.axInitializer.apply(name, arguments);
        return name;
    };
    ASQName.FromMultiname = function (mn) {
        var name = Object.create(this.sec.AXQName.tPrototype);
        name.name = mn;
        return name;
    };
    /**
   * 13.3.1 The QName Constructor Called as a Function
   *
   * QName ( )
   * QName ( Name )
   * QName ( Namespace , Name )
   */
    ASQName.axApply = function (_self, args) {
        var nameOrNS_ = args[0];
        var name_ = args[1];
        // 1. If Namespace is not specified and Type(Name) is Object and Name.[[Class]] == “QName”
        if (args.length === 1 && nameOrNS_ && nameOrNS_.axClass === this.sec.AXQName) {
            // a. Return Name
            return nameOrNS_;
        }
        // 2. Create and return a new QName object exactly as if the QName constructor had been
        // called with the same arguments (section 13.3.2).
        switch (args.length) {
            case 0:
                return this.sec.AXQName.Create();
            case 1:
                return this.sec.AXQName.Create(nameOrNS_);
            default:
                return this.sec.AXQName.Create(nameOrNS_, name_);
        }
    };
    // E4X 11.5.1 The Abstract Equality Comparison Algorithm, step 3.b.
    ASQName.prototype.equals = function (other) {
        return other && other.axClass === this.sec.AXQName &&
            other.uri === this.uri && other.name.name === this.name.name ||
            typeof other === 'string' && this.toString() === other;
    };
    Object.defineProperty(ASQName.prototype, "localName", {
        get: function () {
            return this.name.name != '' ? this.name.name : null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASQName.prototype, "uri", {
        get: function () {
            var namespaces = this.name.namespaces;
            return namespaces.length > 1 ? '' : namespaces[0] ? namespaces[0].uri : null;
        },
        enumerable: false,
        configurable: true
    });
    ASQName.prototype.ecmaToString = function () {
        if (this && this === this.sec.AXQName.dPrototype) {
            return '';
        }
        if (!(this && this.axClass === this.sec.AXQName)) {
            this.sec.throwError('TypeError', Errors.InvokeOnIncompatibleObjectError, 'QName.prototype.toString');
        }
        return this.toString();
    };
    ASQName.prototype.toString = function () {
        var uri = this.uri;
        if (uri === '') {
            return this.name.name;
        }
        if (uri === null) {
            return '*::' + this.name.name;
        }
        uri = uri + '';
        var cc = uri.charCodeAt(uri.length - 1);
        // strip the version mark, if there is one
        var base_uri = uri;
        if (cc >= 0xE000 && cc <= 0xF8FF) {
            base_uri = uri.substr(0, uri.length - 1);
        }
        if (base_uri === '') {
            return this.name.name;
        }
        // causes issues in nitrome-games:
        // return base_uri + "::" + this.name.name;
        return this.name.name;
    };
    ASQName.prototype.valueOf = function () {
        return this;
    };
    Object.defineProperty(ASQName.prototype, "prefix", {
        /**
       * 13.3.5.3 [[Prefix]]
       * The [[Prefix]] property is an optional internal property that is not directly visible to
       * users. It may be used by implementations that preserve prefixes in qualified names. The
       * value of the [[Prefix]] property is a value of type string or undefined. If the [[Prefix]]
       * property is undefined, the prefix associated with this QName is unknown.
       */
        get: function () {
            return this.name.namespaces[0] ? this.name.namespaces[0].prefix : null;
        },
        enumerable: false,
        configurable: true
    });
    return ASQName;
}(ASObject));
export { ASQName };
var ASXML_FLAGS;
(function (ASXML_FLAGS) {
    ASXML_FLAGS[ASXML_FLAGS["FLAG_IGNORE_COMMENTS"] = 1] = "FLAG_IGNORE_COMMENTS";
    ASXML_FLAGS[ASXML_FLAGS["FLAG_IGNORE_PROCESSING_INSTRUCTIONS"] = 2] = "FLAG_IGNORE_PROCESSING_INSTRUCTIONS";
    ASXML_FLAGS[ASXML_FLAGS["FLAG_IGNORE_WHITESPACE"] = 4] = "FLAG_IGNORE_WHITESPACE";
    ASXML_FLAGS[ASXML_FLAGS["FLAG_PRETTY_PRINTING"] = 8] = "FLAG_PRETTY_PRINTING";
    ASXML_FLAGS[ASXML_FLAGS["ALL"] = 15] = "ALL";
})(ASXML_FLAGS || (ASXML_FLAGS = {}));
var ASXMLKindNames = [null, 'element', 'attribute', 'text', 'comment', 'processing-instruction'];
var ASXML = /** @class */ (function (_super) {
    __extends(ASXML, _super);
    function ASXML(value) {
        var _this = _super.call(this) || this;
        _this._parent = null;
        if (isNullOrUndefined(value)) {
            value = '';
        }
        if (typeof value === 'string' && value.length === 0) {
            _this._kind = 3 /* ASXMLKind.Text */;
            _this._value = '';
            return _this;
        }
        var x = toXML(value, _this.sec);
        if (isXMLType(value, _this.sec)) {
            x = x._deepCopy();
        }
        _this._kind = x._kind;
        _this._name = x._name;
        _this._value = x._value;
        _this._attributes = x._attributes;
        _this._inScopeNamespaces = x._inScopeNamespaces;
        var children = x._children;
        _this._children = children;
        if (children) {
            for (var i = 0; i < children.length; i++) {
                var child = children[i];
                child._parent = _this;
            }
        }
        return _this;
    }
    ASXML.classInitializer = function () {
        defineNonEnumerableProperty(this, '$Bglength', 1);
        var proto = this.dPrototype;
        var asProto = ASXML.prototype;
        addPrototypeFunctionAlias(proto, '$BgvalueOf', asProto.valueOf);
        defineNonEnumerableProperty(proto, '$BghasOwnProperty', asProto.native_hasOwnProperty);
        defineNonEnumerableProperty(proto, '$BgpropertyIsEnumerable', asProto.native_propertyIsEnumerable);
        addPrototypeFunctionAlias(this, '$Bgsettings', ASXML.native_settings);
        addPrototypeFunctionAlias(this, '$BgsetSettings', ASXML.native_setSettings);
        addPrototypeFunctionAlias(this, '$BgdefaultSettings', ASXML.native_defaultSettings);
        addPrototypeFunctionAlias(proto, '$BgtoString', asProto.toString);
        addPrototypeFunctionAlias(proto, '$BgaddNamespace', asProto.addNamespace);
        addPrototypeFunctionAlias(proto, '$BgappendChild', asProto.appendChild);
        addPrototypeFunctionAlias(proto, '$Bgattribute', asProto.attribute);
        addPrototypeFunctionAlias(proto, '$Bgattributes', asProto.attributes);
        addPrototypeFunctionAlias(proto, '$Bgchild', asProto.child);
        addPrototypeFunctionAlias(proto, '$BgchildIndex', asProto.childIndex);
        addPrototypeFunctionAlias(proto, '$Bgchildren', asProto.children);
        addPrototypeFunctionAlias(proto, '$Bgcomments', asProto.comments);
        addPrototypeFunctionAlias(proto, '$Bgcontains', asProto.contains);
        addPrototypeFunctionAlias(proto, '$Bgcopy', asProto.copy);
        addPrototypeFunctionAlias(proto, '$Bgdescendants', asProto.descendants);
        addPrototypeFunctionAlias(proto, '$Bgelements', asProto.elements);
        addPrototypeFunctionAlias(proto, '$BghasComplexContent', asProto.hasComplexContent);
        addPrototypeFunctionAlias(proto, '$BghasSimpleContent', asProto.hasSimpleContent);
        addPrototypeFunctionAlias(proto, '$BginScopeNamespaces', asProto.inScopeNamespaces);
        addPrototypeFunctionAlias(proto, '$BginsertChildAfter', asProto.insertChildAfter);
        addPrototypeFunctionAlias(proto, '$BginsertChildBefore', asProto.insertChildBefore);
        addPrototypeFunctionAlias(proto, '$Bglength', asProto.length);
        addPrototypeFunctionAlias(proto, '$BglocalName', asProto.localName);
        addPrototypeFunctionAlias(proto, '$Bgname', asProto.name);
        addPrototypeFunctionAlias(proto, '$Bgnamespace', asProto.namespace);
        addPrototypeFunctionAlias(proto, '$BgnamespaceDeclarations', asProto.namespaceDeclarations);
        addPrototypeFunctionAlias(proto, '$BgnodeKind', asProto.nodeKind);
        addPrototypeFunctionAlias(proto, '$Bgnormalize', asProto.normalize);
        addPrototypeFunctionAlias(proto, '$Bgparent', asProto.parent);
        addPrototypeFunctionAlias(proto, '$BgprocessingInstructions', asProto.processingInstructions);
        addPrototypeFunctionAlias(proto, '$BgprependChild', asProto.prependChild);
        addPrototypeFunctionAlias(proto, '$BgremoveNamespace', asProto.removeNamespace);
        addPrototypeFunctionAlias(proto, '$Bgreplace', asProto.replace);
        addPrototypeFunctionAlias(proto, '$BgsetChildren', asProto.setChildren);
        addPrototypeFunctionAlias(proto, '$BgsetLocalName', asProto.setLocalName);
        addPrototypeFunctionAlias(proto, '$BgsetName', asProto.setName);
        addPrototypeFunctionAlias(proto, '$BgsetNamespace', asProto.setNamespace);
        addPrototypeFunctionAlias(proto, '$Bgtext', asProto.text);
        addPrototypeFunctionAlias(proto, '$BgtoXMLString', asProto.toXMLString);
        addPrototypeFunctionAlias(proto, '$BgtoJSON', asProto.toJSON);
    };
    ASXML.Create = function (value) {
        var xml = Object.create(this.sec.AXXML.tPrototype);
        xml.axInitializer(value);
        return xml;
    };
    ASXML.resetSettings = function () {
        this._flags = ASXML_FLAGS.ALL;
    };
    ASXML.native_settings = function () {
        var settings = Object.create(this.sec.AXObject.tPrototype);
        settings.$BgignoreComments = this.ignoreComments;
        settings.$BgignoreProcessingInstructions = this.ignoreProcessingInstructions;
        settings.$BgignoreWhitespace = this.ignoreWhitespace;
        settings.$BgprettyPrinting = this.prettyPrinting;
        settings.$BgprettyIndent = this.prettyIndent;
        return settings;
    };
    ASXML.native_setSettings = function (o) {
        if (isNullOrUndefined(o)) {
            this.ignoreComments = true;
            this.ignoreProcessingInstructions = true;
            this.ignoreWhitespace = true;
            this.prettyPrinting = true;
            this.prettyIndent = 2;
            return;
        }
        if (typeof o.$BgignoreComments === 'boolean') {
            this.ignoreComments = o.$BgignoreComments;
        }
        if (typeof o.$BgignoreProcessingInstructions === 'boolean') {
            this.ignoreProcessingInstructions = o.$BgignoreProcessingInstructions;
        }
        if (typeof o.$BgignoreWhitespace === 'boolean') {
            this.ignoreWhitespace = o.$BgignoreWhitespace;
        }
        if (o.$BgprettyPrinting === 'boolean') {
            this.prettyPrinting = o.$BgprettyPrinting;
        }
        if (o.$BgprettyIndent === 'number') {
            this.prettyIndent = o.$BgprettyIndent;
        }
    };
    ASXML.native_defaultSettings = function () {
        return {
            __proto__: this.sec.AXObject.tPrototype,
            $BgignoreComments: true,
            $BgignoreProcessingInstructions: true,
            $BgignoreWhitespace: true,
            $BgprettyPrinting: true,
            $BgprettyIndent: 2
        };
    };
    ASXML.axApply = function (self, args) {
        var value = args[0];
        // 13.5.1 The XMLList Constructor Called as a Function
        if (isNullOrUndefined(value)) {
            value = '';
        }
        return toXML(value, this.sec);
    };
    ASXML.prototype.valueOf = function () {
        return this;
    };
    // E4X 11.5.1 The Abstract Equality Comparison Algorithm, steps 1-4.
    ASXML.prototype.equals = function (other) {
        // Steps 1,2.
        if (other && other.axClass === this.sec.AXXMLList) {
            return other.equals(this);
        }
        // Step 3.
        if (other && other.axClass === this.sec.AXXML) {
            // Step 3.a.i.
            var otherXML = other;
            if ((this._kind === 3 /* ASXMLKind.Text */ || this._kind === 2 /* ASXMLKind.Attribute */) &&
                otherXML.hasSimpleContent() ||
                (otherXML._kind === 3 /* ASXMLKind.Text */ || otherXML._kind === 2 /* ASXMLKind.Attribute */) &&
                    this.hasSimpleContent()) {
                return this.toString() === other.toString();
            }
            // Step 3.a.ii.
            return this._deepEquals(other);
            // The rest of step 3 is implemented in {Namespace,QName}.equals and non-E4X parts of the
            // engine.
        }
        // Step 4.
        return this.hasSimpleContent() && this.toString() === axCoerceString(other);
        // The remaining steps are implemented by other means in the interpreter/compiler.
    };
    ASXML.prototype.init = function (kind, mn) {
        this._name = mn;
        this._kind = kind; // E4X [[Class]]
        this._parent = null;
        switch (kind) {
            case 1 /* ASXMLKind.Element */:
                this._inScopeNamespaces = [];
                this._attributes = [];
                this._children = []; // child nodes go here
                break;
            case 4 /* ASXMLKind.Comment */:
            case 5 /* ASXMLKind.ProcessingInstruction */:
            case 2 /* ASXMLKind.Attribute */:
            case 3 /* ASXMLKind.Text */:
                this._value = '';
                break;
            default:
                break;
        }
        return this;
    };
    // 9.1.1.9 [[Equals]] (V)
    ASXML.prototype._deepEquals = function (V) {
        // Step 1.
        if (!V || V.axClass !== this.sec.AXXML) {
            return false;
        }
        var other = V;
        // Step 2.
        if (this._kind !== other._kind) {
            return false;
        }
        // Steps 3-4.
        if (!!this._name !== !!other._name || (this._name && !this._name.equalsQName(other._name))) {
            return false;
        }
        // Not in the spec, but a substantial optimization.
        if (this._kind !== 1 /* ASXMLKind.Element */) {
            // Step 7.
            // This only affects non-Element nodes, so moved up here.
            if (this._value !== other._value) {
                return false;
            }
            return true;
        }
        // Step 5.
        var attributes = this._attributes;
        var otherAttributes = other._attributes;
        if (attributes.length !== otherAttributes.length) {
            return false;
        }
        // Step 6.
        var children = this._children;
        var otherChildren = other._children;
        if (children.length !== otherChildren.length) {
            return false;
        }
        // Step 8.
        attribOuter: for (var i = 0; i < attributes.length; i++) {
            var attribute = attributes[i];
            for (var j = 0; j < otherAttributes.length; j++) {
                var otherAttribute = otherAttributes[j];
                if (otherAttribute._name.equalsQName(attribute._name) &&
                    otherAttribute._value === attribute._value) {
                    continue attribOuter;
                }
            }
            return false;
        }
        // Step 9.
        for (var i = 0; i < children.length; i++) {
            if (!children[i].equals(otherChildren[i])) {
                return false;
            }
        }
        // Step 10.
        return true;
    };
    // 9.1.1.7 [[DeepCopy]] ( )
    ASXML.prototype._deepCopy = function () {
        var kind = this._kind;
        var clone = this.sec.AXXML.Create();
        clone._kind = kind;
        clone._name = this._name;
        switch (kind) {
            case 1 /* ASXMLKind.Element */:
                clone._inScopeNamespaces = this._inScopeNamespaces.slice();
                clone._attributes = this._attributes.map(function (attr) {
                    attr = attr._deepCopy();
                    attr._parent = clone;
                    return attr;
                });
                clone._children = this._children.map(function (child) {
                    child = child._deepCopy();
                    child._parent = clone;
                    return child;
                });
                break;
            case 4 /* ASXMLKind.Comment */:
            case 5 /* ASXMLKind.ProcessingInstruction */:
            case 2 /* ASXMLKind.Attribute */:
            case 3 /* ASXMLKind.Text */:
                clone._value = this._value;
                break;
            default:
                break;
        }
        return clone;
    };
    // 9.1.1.10 [[ResolveValue]] ( )
    ASXML.prototype.resolveValue = function () {
        return this;
    };
    ASXML.prototype._addInScopeNamespace = function (ns) {
        if (this._inScopeNamespaces.some(function (ins) { return ins.uri === ns.uri && ins.prefix === ns.prefix; })) {
            return;
        }
        this._inScopeNamespaces.push(ns);
    };
    Object.defineProperty(ASXML, "ignoreComments", {
        get: function () {
            return !!(this._flags & ASXML_FLAGS.FLAG_IGNORE_COMMENTS);
        },
        set: function (newIgnore) {
            newIgnore = !!newIgnore;
            if (newIgnore) {
                this._flags |= ASXML_FLAGS.FLAG_IGNORE_COMMENTS;
            }
            else {
                this._flags &= ~ASXML_FLAGS.FLAG_IGNORE_COMMENTS;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASXML, "ignoreProcessingInstructions", {
        get: function () {
            return !!(this._flags & ASXML_FLAGS.FLAG_IGNORE_PROCESSING_INSTRUCTIONS);
        },
        set: function (newIgnore) {
            newIgnore = !!newIgnore;
            if (newIgnore) {
                this._flags |= ASXML_FLAGS.FLAG_IGNORE_PROCESSING_INSTRUCTIONS;
            }
            else {
                this._flags &= ~ASXML_FLAGS.FLAG_IGNORE_PROCESSING_INSTRUCTIONS;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASXML, "ignoreWhitespace", {
        get: function () {
            return !!(this._flags & ASXML_FLAGS.FLAG_IGNORE_WHITESPACE);
        },
        set: function (newIgnore) {
            newIgnore = !!newIgnore;
            if (newIgnore) {
                this._flags |= ASXML_FLAGS.FLAG_IGNORE_WHITESPACE;
            }
            else {
                this._flags &= ~ASXML_FLAGS.FLAG_IGNORE_WHITESPACE;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASXML, "prettyPrinting", {
        get: function () {
            return !!(this._flags & ASXML_FLAGS.FLAG_PRETTY_PRINTING);
        },
        set: function (newPretty) {
            newPretty = !!newPretty;
            if (newPretty) {
                this._flags |= ASXML_FLAGS.FLAG_PRETTY_PRINTING;
            }
            else {
                this._flags &= ~ASXML_FLAGS.FLAG_PRETTY_PRINTING;
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ASXML, "prettyIndent", {
        get: function () {
            return this._prettyIndent;
        },
        set: function (newIndent /*int*/) {
            newIndent = newIndent | 0;
            this._prettyIndent = newIndent;
        },
        enumerable: false,
        configurable: true
    });
    ASXML.prototype.toString = function () {
        if (this === this.axClass.dPrototype) {
            return '';
        }
        if (this.hasComplexContent()) {
            return this.toXMLString();
        }
        return toString(this, this.sec);
    };
    // 13.4.4.14 XML.prototype.hasOwnProperty ( P )
    ASXML.prototype.native_hasOwnProperty = function (P) {
        if (this === this.axClass.dPrototype) {
            return ASObject.prototype.native_hasOwnProperty.call(this, P);
        }
        var mn = toXMLName(P, this.sec);
        if (this.hasProperty(mn)) {
            return true;
        }
        return this.axHasOwnProperty(mn);
    };
    // 13.4.4.30 XML.prototype.propertyIsEnumerable ( P )
    ASXML.prototype.native_propertyIsEnumerable = function (P) {
        if (P === void 0) { P = undefined; }
        return String(P) === '0';
    };
    ASXML.prototype.addNamespace = function (ns) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.2 XML.prototype.addNamespace ( namespace )
        this._addInScopeNamespace(ns);
        return this;
    };
    ASXML.prototype.appendChild = function (child) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        this.insert(this._children.length, child);
        return this;
    };
    ASXML.prototype.attribute = function (arg) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        if (isNullOrUndefined(arg) && arguments.length > 0) {
            this.sec.throwError('TypeError', Errors.ConvertUndefinedToObjectError);
        }
        if (arg && arg.axClass === this.sec.AXQName) {
            return this.getProperty(arg.name);
        }
        arg = axCoerceString(arg);
        if (arg === '*' || arguments.length === 0) {
            arg = null;
        }
        tmpMultiname.name = arg;
        tmpMultiname.namespaces = [Namespace.PUBLIC];
        tmpMultiname.kind = 13 /* CONSTANT.QNameA */;
        return this.getProperty(tmpMultiname);
    };
    ASXML.prototype.attributes = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        var list = this.sec.AXXMLList.CreateList(this, this._name);
        Array.prototype.push.apply(list._children, this._attributes);
        return list;
    };
    // 13.4.4.6
    ASXML.prototype.child = function (propertyName /* string|number|QName */) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // Step 1.
        if (isIndex(propertyName)) {
            var list = this.sec.AXXMLList.CreateList(null, null);
            if (this._children && propertyName < this._children.length) {
                list.append(this._children[propertyName | 0]);
            }
            return list;
        }
        // Steps 2-3.
        var mn;
        if (propertyName && propertyName.axClass === this.sec.AXQName) {
            mn = propertyName.name;
        }
        else {
            mn = tmpMultiname;
            mn.kind = 7 /* CONSTANT.QName */;
            mn.name = toString(propertyName, this.sec);
        }
        return this.getProperty(mn);
    };
    ASXML.prototype.childIndex = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.7 XML.prototype.childIndex ( )
        if (!this._parent || this._kind === 2 /* ASXMLKind.Attribute */) {
            return -1;
        }
        return this._parent._children.indexOf(this);
    };
    ASXML.prototype.children = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        var xl = this.sec.AXXMLList.CreateList(this, this._name);
        xl._children = this._children.concat();
        return xl;
    };
    ASXML.prototype.comments = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.9 XML.prototype.comments ( )
        var xl = this.sec.AXXMLList.CreateList(this, this._name);
        this._children && this._children.forEach(function (v, i) {
            if (v._kind === 4 /* ASXMLKind.Comment */) {
                xl.append(v);
            }
        });
        return xl;
    };
    ASXML.prototype.contains = function (value) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.10 XML.prototype.contains ( value )
        return this === value;
    };
    ASXML.prototype.copy = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        return this._deepCopy();
    };
    // 9.1.1.8 [[Descendants]] (P)
    ASXML.prototype.descendants = function (name) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        var xl = this.sec.AXXMLList.CreateList(this, this._name);
        return this.descendantsInto(toXMLName(name, this.sec), xl);
    };
    ASXML.prototype.elements = function (name) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.13 XML.prototype.elements ( [ name ] )
        return this.getProperty(toXMLName(name, this.sec));
    };
    ASXML.prototype.hasComplexContent = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.15 XML.prototype.hasComplexContent( )
        if (this._kind === 2 /* ASXMLKind.Attribute */ ||
            this._kind === 4 /* ASXMLKind.Comment */ ||
            this._kind === 5 /* ASXMLKind.ProcessingInstruction */ ||
            this._kind === 3 /* ASXMLKind.Text */) {
            return false;
        }
        return this._children.some(function (child) {
            return child._kind === 1 /* ASXMLKind.Element */;
        });
    };
    ASXML.prototype.hasSimpleContent = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // 13.4.4.16 XML.prototype.hasSimpleContent()
        if (this._kind === 4 /* ASXMLKind.Comment */ ||
            this._kind === 5 /* ASXMLKind.ProcessingInstruction */) {
            return false;
        }
        if (this._kind !== 1 /* ASXMLKind.Element */) {
            return true;
        }
        if (!this._children && this._children.length === 0) {
            return true;
        }
        return this._children.every(function (child) {
            return child._kind !== 1 /* ASXMLKind.Element */;
        });
    };
    // 13.4.4.17
    ASXML.prototype.inScopeNamespaces = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        var namespaces = this._inScopeNamespacesImpl();
        var result = [];
        for (var i = 0; i < namespaces.length; i++) {
            var AXNamespace = this.sec.AXNamespace;
            result[i] = AXNamespace.FromNamespace(namespaces[i]);
        }
        return this.sec.AXArray.axBox(result);
    };
    ASXML.prototype._inScopeNamespacesImpl = function () {
        // Step 1.
        var y = this;
        // Step 2.
        var inScopeNS = [];
        var inScopeNSMap = inScopeNS;
        // Step 3.
        while (y !== null) {
            // Step 3.a.
            var namespaces = y._inScopeNamespaces;
            for (var i = 0; namespaces && i < namespaces.length; i++) {
                var ns = namespaces[i];
                if (!inScopeNSMap[ns.prefix]) {
                    inScopeNSMap[ns.prefix] = ns;
                    inScopeNS.push(ns);
                }
            }
            // Step 3.b.
            y = y._parent;
        }
        return inScopeNS;
    };
    // 13.4.4.18
    ASXML.prototype.insertChildAfter = function (child1, child2) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // Step 1.
        if (this._kind > 1 /* ASXMLKind.Element */) {
            return;
        }
        // Step 2.
        if (child1 == null) {
            this.insert(0, child2);
            return this;
        }
        // Step 3.
        // The spec doesn't mention it, but Tamarin seems to unpack single-entry XMLLists here.
        if (child1.axClass === this.sec.AXXMLList && child1._children.length === 1) {
            child1 = child1._children[0];
        }
        if (child1.axClass === this.sec.AXXML) {
            for (var i = 0; i < this._children.length; i++) {
                var child = this._children[i];
                if (child === child1) {
                    this.insert(i + 1, child2);
                    return this;
                }
            }
        }
        // Step 4 (implicit).
    };
    // 13.4.4.19
    ASXML.prototype.insertChildBefore = function (child1, child2) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // Step 1.
        if (this._kind > 1 /* ASXMLKind.Element */) {
            return;
        }
        // Step 2.
        if (child1 == null) {
            this.insert(this._children.length, child2);
            return this;
        }
        // Step 3.
        // The spec doesn't mention it, but Tamarin seems to unpack single-entry XMLLists here.
        if (child1.axClass === this.sec.AXXMLList && child1._children.length === 1) {
            child1 = child1._children[0];
        }
        if (child1.axClass === this.sec.AXXML) {
            for (var i = 0; i < this._children.length; i++) {
                var child = this._children[i];
                if (child === child1) {
                    this.insert(i, child2);
                    return this;
                }
            }
        }
        // Step 4 (implicit).
    };
    // XML.[[Length]]
    ASXML.prototype.length = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        if (!this._children) {
            return 0;
        }
        return this._children.length;
    };
    ASXML.prototype.localName = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        return this._name.name != '' ? this._name.name : null;
    };
    ASXML.prototype.name = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        return this.sec.AXQName.FromMultiname(this._name);
    };
    // 13.4.4.23
    ASXML.prototype.namespace = function (prefix) {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // Step 4.a.
        if (arguments.length === 0 && this._kind >= 3 /* ASXMLKind.Text */) {
            return null;
        }
        // Steps 1-3.
        var inScopeNS = this._inScopeNamespacesImpl();
        // Step 4.
        if (arguments.length === 0) {
            // Step 4.b.
            return this.sec.AXNamespace.FromNamespace(GetNamespace(this._name, inScopeNS));
        }
        // Step 5.a.
        prefix = axCoerceString(prefix);
        // Step 5.b-c.
        for (var i = 0; i < inScopeNS.length; i++) {
            var ns = inScopeNS[i];
            if (ns.prefix === prefix) {
                return this.sec.AXNamespace.FromNamespace(ns);
            }
        }
        // Step 5.b alternate clause implicit.
    };
    ASXML.prototype.namespaceDeclarations = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        release || release || notImplemented('public.XML::namespaceDeclarations');
        return;
    };
    ASXML.prototype.nodeKind = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        return ASXMLKindNames[this._kind];
    };
    ASXML.prototype.normalize = function () {
        if (!this || this.axClass !== this.sec.AXXML) {
            this.sec.throwError('TypeError', Errors.CheckTypeFailedError, this, 'XML');
        }
        // Steps 1-2.
        for (var i = 0; i < this._children.length;) {
            var child = this._children[i];
            // Step 2.a.
            if (child._kind === 1 /* ASXMLKind.Element */) {
                child.normalize();
                i++;
                // Step 2.b.
            }
            else if (child._kind === 3 /* ASXMLKind.Text */) {
                // Step 2.b.i.
                while (i + 1 < this._children.length) {
                    var nextChild = this._children[i + 1];
                    if (nextChild._kind !== 3 /* ASXMLKind.Text */) {
                        break;
                    }
                    child._value += nextChild._value;
                    this.removeByIndex(i + 1);
                }
                // Step 2.b.ii.
                // The spec says to only remove 0-length nodes, but Tamarin removes whitespace-only
                // nodes, too.
                if (child._value.length === 0 || isWhitespaceString(child._value)) {
                    this.removeByIndex(i);
                }
                else { // Step 2.b.iii.
                    i++;
                }
            }
            else { // Step 2.c.
                i++;
            }
        }
        return this;
    };
    ASXML.prototype.removeByIndex = function (index) {
        var child = this._children[index];
        child._parent = null;
        this._children.splice(index, 1);
    };
    ASXML.prototype.parent = function () {
        // Absurdly, and in difference to what the spec says, parent() returns `undefined` for null.
        return this._parent || undefined;
    };
    // 13.4.4.28 XML.prototype.processingInstructions ( [ name ] )
    ASXML.prototype.processingInstructions = function (name) {
        // Step 1 (implicit).
        // Step 3.
        var list = this.sec.AXXMLList.CreateList(this, this._name);
        list._targetObject = this;
        list._targetProperty = null;
        // Steps 2,4-5.
        return this.processingInstructionsInto(toXMLName(name, this.sec), list);
    };
    ASXML.prototype.processingInstructionsInto = function (name, list) {
        var localName = name || '*';
        // Step 4.
        var children = this._children;
        if (!children) {
            return list;
        }
        for (var i = 0; i < children.length; i++) {
            var child = children[i];
            if (child._kind === 5 /* ASXMLKind.ProcessingInstruction */ &&
                (localName === '*' || child._name.name === localName)) {
                list._children.push(child);
            }
        }
        // Step 5.
        return list;
    };
    // 13.4.4.29
    ASXML.prototype.prependChild = function (child) {
        this.insert(0, child);
        return this;
    };
    ASXML.prototype.removeNamespace = function (ns) {
        release || release || notImplemented('public.XML::removeNamespace');
        return;
    };
    // 13.4.4.32 XML.prototype.replace
    ASXML.prototype.replace = function (propertyName, value) {
        // Step 1.
        if (this._kind !== 1 /* ASXMLKind.Element */) {
            return;
        }
        var c;
        // Step 2.
        if (!value || value.axClass !== this.axClass && value.axClass !== this.sec.AXXMLList) {
            c = axCoerceString(value);
            // Step 3.
        }
        else {
            c = value._deepCopy();
        }
        // Step 4.
        var mn = toXMLName(propertyName, this.sec);
        if (isIndex(mn.name)) {
            this._replaceByIndex(mn.name | 0, c);
            // Step 10.
            return this;
        }
        var isAnyName = mn.isAnyName();
        // Step 5 (Implicit).
        // Step 6.
        var i = -1;
        // Step 7.
        var children = this._children;
        for (var k = children.length; k--;) {
            // Step 7.a.
            var child = children[k];
            if (isAnyName || child._kind === 1 /* ASXMLKind.Element */ && child._name.matches(mn)) {
                // Step 7.a.i.
                if (i !== -1) {
                    this.deleteByIndex(i);
                }
                // Step 7.a.i.
                i = k;
            }
        }
        // Step 8.
        if (i === -1) {
            // Step 10.
            return this;
        }
        // Step 9.
        this._replaceByIndex(i, c);
        // Step 10.
        return this;
    };
    // 9.1.1.12 [[Replace]] (P, V)
    ASXML.prototype._replaceByIndex = function (p, v) {
        // Step 1.
        if (this._kind > 1 /* ASXMLKind.Element */) {
            return this;
        }
        // Steps 2-3. (Implicit, guaranteed by assert).
        release || assert(typeof p === 'number' && p >>> 0 === p);
        // Step 4.
        var children = this._children;
        if (p > children.length) {
            p = children.length;
        }
        // Step 5.
        if (v && v.axClass === this.axClass && v._kind !== 2 /* ASXMLKind.Attribute */) {
            // Step 5.a.
            if (v._kind === 1 /* ASXMLKind.Element */) {
                var a = this;
                while (a) {
                    if (a === v) {
                        this.sec.throwError('Error', Errors.XMLIllegalCyclicalLoop);
                    }
                    a = a._parent;
                }
            }
            // Step 5.b.
            v._parent = this;
            // Step 5.c.
            if (children[p]) {
                children[p]._parent = null;
            }
            // Step 5.d.
            children[p] = v;
            // Step 6.
        }
        else if (v && v.axClass === this.sec.AXXMLList) {
            // Inlined steps.
            if (v._children.length === 0) {
                this.deleteByIndex(p);
            }
            else {
                var n = v._children.length;
                if (p < children.length) {
                    children[p]._parent = null;
                    for (var i = children.length - 1; i > p; i--) {
                        children[i + n] = children[i];
                    }
                }
                for (var i = 0; i < n; i++) {
                    var child = v._children[i];
                    child._parent = this;
                    children[i + p] = child;
                }
            }
            // Step 7.
        }
        else {
            // Step 7.a.
            var s = axCoerceString(v);
            // Step 7.b.
            var t = this.axClass.Create();
            t._kind = 3 /* ASXMLKind.Text */;
            t._value = s;
            t._parent = this;
            // Step 7.c.
            if (children[p]) {
                children[p]._parent = null;
            }
            // Step 7.d.
            children[p] = t;
        }
    };
    ASXML.prototype.setChildren = function (value) {
        this.setProperty(anyMultiname, value);
        return this;
    };
    // 13.4.4.34 XML.prototype.setLocalName( name )
    ASXML.prototype.setLocalName = function (name_) {
        // Step 1.
        if (this._kind === 3 /* ASXMLKind.Text */ || this._kind === 4 /* ASXMLKind.Comment */) {
            return;
        }
        var name;
        // Step 2.
        if (name_ && name_.axClass === this.sec.AXQName) {
            name = name_.localName;
        }
        else {
            // Step 3.
            name = axCoerceString(name_);
        }
        // Step 4.
        this._name.name = name;
    };
    // 13.4.4.35 XML.prototype.setName( name )
    ASXML.prototype.setName = function (name_) {
        // Step 1.
        if (this._kind === 3 /* ASXMLKind.Text */ || this._kind === 4 /* ASXMLKind.Comment */) {
            return;
        }
        // Step 2.
        if (name_ && name_.axClass === this.sec.AXQName && name_.uri === null) {
            name_ = name_.localName;
        }
        // Step 3.
        var name = this.sec.AXQName.Create(name_).name;
        // Step 4.
        if (this._kind === 5 /* ASXMLKind.ProcessingInstruction */) {
            release || assert(name.namespaces[0].type === 0 /* NamespaceType.Public */);
            name.namespaces[0] = Namespace.PUBLIC;
        }
        // Step 5.
        this._name = name;
        // Steps 6-8.
        var node = this;
        if (this._kind === 2 /* ASXMLKind.Attribute */) {
            if (this._parent === null) {
                return;
            }
            node = this._parent;
        }
        node.addInScopeNamespace(name.namespaces[0]);
    };
    ASXML.prototype.setNamespace = function (ns) {
        // Step 1.
        if (this._kind === 3 /* ASXMLKind.Text */ || this._kind === 4 /* ASXMLKind.Comment */ ||
            this._kind === 5 /* ASXMLKind.ProcessingInstruction */) {
            return;
        }
        // Step 2.
        var ns2 = this.sec.AXNamespace.Create(ns)._ns;
        // Step 3.
        this._name.namespaces = [ns2];
        // Step 4.
        if (this._kind === 2 /* ASXMLKind.Attribute */) {
            if (this._parent) {
                this._parent.addInScopeNamespace(ns2);
            }
            // Step 5.
        }
        else if (this._kind === 1 /* ASXMLKind.Element */) {
            this.addInScopeNamespace(ns2);
        }
    };
    ASXML.prototype.text = function () {
        // 13.4.4.37 XML.prototype.text ( );
        var xl = this.sec.AXXMLList.CreateList(this, this._name);
        this._children && this._children.forEach(function (v) {
            if (v._kind === 3 /* ASXMLKind.Text */) {
                xl.append(v);
            }
        });
        return xl;
    };
    ASXML.prototype.toXMLString = function () {
        return this.toXMLStringImpl();
    };
    ASXML.prototype.toXMLStringImpl = function (ancestorNamespaces, indentLevel) {
        var node = this;
        var sec = this.sec;
        // 10.2.1 ToXMLString Applied to the XML Type
        var prettyPrinting = sec.AXXML.prettyPrinting;
        indentLevel |= 0;
        var s = prettyPrinting ? getIndentString(indentLevel) : '';
        var kind = node._kind;
        switch (kind) {
            // 4. If x.[[Class]] == "text",
            case 3 /* ASXMLKind.Text */:
                return prettyPrinting ?
                    s + escapeElementValue(sec, trimWhitespaces(node._value)) :
                    escapeElementValue(sec, node._value);
            // 5. If x.[[Class]] == "attribute", return the result of concatenating s and
            // EscapeAttributeValue(x.[[Value]])
            case 2 /* ASXMLKind.Attribute */:
                return s + escapeAttributeValue(node._value);
            // 6. If x.[[Class]] == "comment", return the result of concatenating s, the string "<!--",
            // x.[[Value]] and the string "-->"
            case 4 /* ASXMLKind.Comment */:
                return s + '<!--' + node._value + '-->';
            // 7 If x.[[Class]] == "processing-instruction", return the result of concatenating s, the
            // string "<?", x.[[Name]].localName, the space <SP> character, x.[[Value]] and the string
            // "?>"
            case 5 /* ASXMLKind.ProcessingInstruction */:
                return s + '<?' + node._name.name + ' ' + node._value + '?>';
            default:
                release || assert(kind === 1 /* ASXMLKind.Element */);
                break;
        }
        ancestorNamespaces = ancestorNamespaces || [];
        var namespaceDeclarations = [];
        var _loop_1 = function (i) {
            var ns = node._inScopeNamespaces[i];
            if (ancestorNamespaces.every(function (ans) { return ans.uri !== ns.uri || ans.prefix !== ns.prefix; })) {
                namespaceDeclarations.push(ns);
            }
        };
        // 10. For each ns in x.[[InScopeNamespaces]]
        for (var i = 0; node._inScopeNamespaces && i < node._inScopeNamespaces.length; i++) {
            _loop_1(i);
        }
        // 11. For each name in the set of names consisting of x.[[Name]] and the name of each
        // attribute in x.[[Attributes]]
        var currentNamespaces = ancestorNamespaces.concat(namespaceDeclarations);
        var namespace = GetNamespace(node._name, currentNamespaces);
        if (namespace.prefix === undefined) {
            // Let namespace.prefix be an arbitrary implementation defined namespace prefix, such that
            // there is no ns2 ∈ (AncestorNamespaces ∪ namespaceDeclarations) with namespace.prefix ==
            // ns2.prefix
            var newPrefix = generateUniquePrefix(currentNamespaces);
            var ns2 = internPrefixedNamespace(0 /* NamespaceType.Public */, namespace.uri, newPrefix);
            // Let namespaceDeclarations = namespaceDeclarations ∪ { namespace }
            namespaceDeclarations.push(ns2);
            currentNamespaces.push(ns2);
        }
        // 12. Let s be the result of concatenating s and the string "<"
        // 13. If namespace.prefix is not the empty string,
        //   a. Let s be the result of concatenating s, namespace.prefix and the string ":"
        // 14. Let s be the result of concatenating s and x.[[Name]].localName
        var elementName = (namespace.prefix ? namespace.prefix + ':' : '') + node._name.name;
        s += '<' + elementName;
        node._attributes && node._attributes.forEach(function (attr) {
            var name = attr._name;
            namespace = GetNamespace(name, currentNamespaces);
            if (namespace.prefix === undefined) {
                // Let namespace.prefix be an arbitrary implementation defined namespace prefix, such that
                // there is no ns2 ∈ (AncestorNamespaces ∪ namespaceDeclarations) with namespace.prefix ==
                // ns2.prefix
                var newPrefix = generateUniquePrefix(currentNamespaces);
                var ns2 = internPrefixedNamespace(0 /* NamespaceType.Public */, namespace.uri, newPrefix);
                // Let namespaceDeclarations = namespaceDeclarations ∪ { namespace }
                namespaceDeclarations.push(ns2);
                currentNamespaces.push(ns2);
            }
        });
        for (var i = 0; i < namespaceDeclarations.length; i++) {
            namespace = namespaceDeclarations[i];
            if (namespace.uri === '' || namespace.uri === 'default') {
                continue;
            }
            var attributeName = namespace.prefix ? 'xmlns:' + namespace.prefix : 'xmlns';
            s += ' ' + attributeName + '="' + escapeAttributeValue(namespace.uri) + '"';
        }
        node._attributes && node._attributes.forEach(function (attr) {
            var name = attr._name;
            var namespace = GetNamespace(name, ancestorNamespaces);
            var attributeName = namespace.prefix ? namespace.prefix + ':' + name.name : name.name;
            s += ' ' + attributeName + '="' + escapeAttributeValue(attr._value) + '"';
        });
        // 17. If x.[[Length]] == 0
        if (node._children.length === 0) {
            //   a. Let s be the result of concatenating s and "/>"
            s += '/>';
            //   b. Return s
            return s;
        }
        // 18. Let s be the result of concatenating s and the string ">"
        s += '>';
        // 19. Let indentChildren = ((x.[[Length]] > 1) or (x.[[Length]] == 1 and x[0].[[Class]] is
        // not equal to "text"))
        var indentChildren = node._children.length > 1 ||
            (node._children.length === 1 && node._children[0]._kind !== 3 /* ASXMLKind.Text */);
        var nextIndentLevel = (prettyPrinting && indentChildren) ?
            indentLevel + sec.AXXML._prettyIndent : 0;
        node._children.forEach(function (childNode) {
            if (prettyPrinting && indentChildren) {
                s += '\n';
            }
            s += childNode.toXMLStringImpl(currentNamespaces, nextIndentLevel);
        });
        if (prettyPrinting && indentChildren) {
            s += '\n' + getIndentString(indentLevel);
        }
        s += '</' + elementName + '>';
        return s;
    };
    ASXML.prototype.toJSON = function (_k) {
        return 'XML';
    };
    ASXML.prototype.axGetEnumerableKeys = function () {
        if (this === this.axClass.dPrototype) {
            return _super.prototype.axGetEnumerableKeys.call(this);
        }
        var keys = [];
        for (var i = 0; i < this._children.length; i++) {
            keys.push(this._children[i]._name.name);
        }
        return keys;
    };
    // 9.1.1.2 [[Put]] (P, V)
    ASXML.prototype.setProperty = function (mn, v) {
        // Step 1. (Step 3 in Tamarin source.)
        var sec = this.sec;
        if (!mn.isAnyName() && !mn.isAttribute() && mn.name === mn.name >>> 0) {
            sec.throwError('TypeError', Errors.XMLAssignmentToIndexedXMLNotAllowed);
        }
        // Step 2. (Step 4 in Tamarin source.)
        if (this._kind === 3 /* ASXMLKind.Text */ || this._kind === 4 /* ASXMLKind.Comment */ ||
            this._kind === 5 /* ASXMLKind.ProcessingInstruction */ || this._kind === 2 /* ASXMLKind.Attribute */) {
            return;
        }
        // Step 3.
        var c;
        if (!isXMLType(v, sec) ||
            v._kind === 3 /* ASXMLKind.Text */ ||
            v._kind === 2 /* ASXMLKind.Attribute */) {
            c = toString(v, sec);
            // Step 4.
        }
        else {
            c = v._deepCopy();
        }
        // Step 5 (implicit, mn is always a Multiname here).
        // Step 6 (7 in Tamarin).
        if (mn.isAttribute()) {
            // Step 6.a (omitted, as in Tamarin).
            // Step 6.b.
            if (c && c.axClass === sec.AXXMLList) {
                // Step 6.b.i.
                if (c._children.length === 0) {
                    c = '';
                    // Step 6.b.ii.
                }
                else {
                    // Step 6.b.ii.1.
                    var s = toString(c._children[0], sec);
                    // Step 6.b.ii.2.
                    for (var j = 1; j < c._children.length; j++) {
                        s += ' ' + toString(c._children[j], sec);
                    }
                    // Step 6.b.ii.3.
                    c = s;
                }
                // Step 6.c.
            }
            else {
                c = axCoerceString(c);
            }
            // Step 6.d.
            var a = null;
            // Step 6.e.
            var attributes = this._attributes;
            var newAttributes = this._attributes = [];
            for (var j = 0; attributes && j < attributes.length; j++) {
                var attribute = attributes[j];
                if (attribute._name.matches(mn)) {
                    // Step 6.e.1.
                    if (!a) {
                        a = attribute;
                        // Step 6.e.2.
                    }
                    else {
                        attribute._parent = null;
                        continue;
                    }
                }
                newAttributes.push(attribute);
            }
            // Step 6.f.
            if (!a) {
                // Wildcard attribute names shouldn't cause any attributes to be *added*, so we can bail
                // here. Tamarin doesn't do this, and it's not entirely clear to me how they avoid
                // adding attributes, but this works and doesn't regress any tests.
                if (mn.isAnyName()) {
                    return;
                }
                var uri = '';
                // 80pro - added the "&& mn.namespaces[0]"" ,
                // because it appears sometimes mn.namespaces[0] can be undefined
                if (mn.namespaces.length === 1 && mn.namespaces[0]) {
                    uri = mn.namespaces[0].uri;
                }
                a = createXML(sec, 2 /* ASXMLKind.Attribute */, uri, mn.name);
                a._parent = this;
                newAttributes.push(a);
                // TODO: implement the namespace parts of step f.
            }
            // Step 6.g.
            a._value = c;
            // Step 6.h.
            return;
        }
        var i;
        var isAny = mn.isAnyName();
        var primitiveAssign = !isXMLType(c, sec) && !isAny && mn.name !== '*';
        var isAnyNamespace = mn.isAnyNamespace();
        for (var k = this._children.length - 1; k >= 0; k--) {
            if ((isAny || this._children[k]._kind === 1 /* ASXMLKind.Element */ &&
                this._children[k]._name.name === mn.name) &&
                (isAnyNamespace ||
                    this._children[k]._kind === 1 /* ASXMLKind.Element */ &&
                        this._children[k]._name.matches(mn))) {
                if (i !== undefined) {
                    this.deleteByIndex(i);
                }
                i = k;
            }
        }
        if (i === undefined) {
            i = this._children.length;
            if (primitiveAssign) {
                var ns = mn.namespaces[0];
                var uri = null;
                var prefix = void 0;
                if (ns.uri !== null) {
                    uri = ns.uri;
                    prefix = ns.prefix;
                }
                if (uri === null) {
                    var defaultNamespace = getDefaultNamespace(sec);
                    uri = defaultNamespace.uri;
                    prefix = defaultNamespace.prefix;
                }
                var y = createXML(sec, 1 /* ASXMLKind.Element */, uri, mn.name, prefix);
                y._parent = this;
                this._replaceByIndex(i, y);
                y.addInScopeNamespace(y._name.namespace);
            }
        }
        if (primitiveAssign) {
            // Blow away kids of x[i].
            var subChildren = this._children[i]._children;
            for (var j = subChildren.length; j--;) {
                subChildren[j]._parent = null;
            }
            subChildren.length = 0;
            var s = toString(c, sec);
            if (s !== '') {
                this._children[i]._replaceByIndex(0, s);
            }
        }
        else {
            this._replaceByIndex(i, c);
        }
    };
    ASXML.prototype.axSetProperty = function (mn, value, _bc) {
        if (this === this.axClass.dPrototype) {
            release || checkValue(value);
            this[this.axResolveMultiname(mn)] = value;
            return;
        }
        this.setProperty(coerceE4XMultiname(mn, this.sec), value);
    };
    // 9.1.1.1 XML.[[Get]] (P)
    ASXML.prototype.getProperty = function (mn) {
        // Step 1.
        var nm = mn.name;
        if (isIndex(nm)) {
            // this is a shortcut to the E4X logic that wants us to create a new
            // XMLList with of size 1 and access it with the given index.
            if ((nm | 0) === 0) {
                return this;
            }
            return null;
        }
        // Step 2 (implicit).
        // Step 3.
        var list = this.sec.AXXMLList.CreateList(this, mn);
        var length = 0;
        var anyName = mn.isAnyName();
        var anyNamespace = mn.isAnyNamespace();
        // Step 4.
        if (mn.isAttribute()) {
            for (var i = 0; this._attributes && i < this._attributes.length; i++) {
                var v = this._attributes[i];
                if ((anyName || v._name.name === nm) &&
                    (anyNamespace || v._name.matches(mn))) {
                    list._children[length++] = v;
                    assert(list._children[0]);
                }
            }
            return list;
        }
        // Step 5.
        for (var i = 0; this._children && i < this._children.length; i++) {
            var v = this._children[i];
            if ((anyName || v._kind === 1 /* ASXMLKind.Element */ && v._name.name === nm) &&
                ((anyNamespace || v._name.matches(mn)))) {
                list._children[length++] = v;
                assert(list._children[0]);
            }
        }
        // Step 6.
        return list;
    };
    ASXML.prototype.axGetProperty = function (mn) {
        if (this === this.axClass.dPrototype) {
            var value = this[this.axResolveMultiname(mn)];
            release || checkValue(value);
            return value;
        }
        return this.getProperty(coerceE4XMultiname(mn, this.sec));
    };
    // 9.1.1.6 [[HasProperty]] (P) (well, very roughly)
    ASXML.prototype.hasProperty = function (mn) {
        if (isIndex(mn.name)) {
            // this is a shortcut to the E4X logic that wants us to create a new
            // XMLList of size 1 and access it with the given index.
            return (mn.name | 0) === 0;
        }
        var name = toXMLName(mn, this.sec);
        var anyName = name.isAnyName();
        var anyNamespace = name.isAnyNamespace();
        if (mn.isAttribute()) {
            for (var i = 0; this._attributes && i < this._attributes.length; i++) {
                var v = this._attributes[i];
                if (anyName || v._name.matches(name)) {
                    return true;
                }
            }
            return false;
        }
        for (var i = 0; this._children && i < this._children.length; i++) {
            var v = this._children[i];
            if ((anyName || v._kind === 1 /* ASXMLKind.Element */ && v._name.name === name.name) &&
                (anyNamespace || v._kind === 1 /* ASXMLKind.Element */ && v._name.matches(name))) {
                return true;
            }
        }
        return false;
    };
    ASXML.prototype.deleteProperty = function (mn) {
        if (isIndex(mn.name)) {
            // This hasn't ever been implemented and silently does nothing in Tamarin (and Rhino).
            return true;
        }
        var name = toXMLName(mn, this.sec);
        var localName = name.name;
        var anyName = mn.isAnyName();
        var anyNamespace = mn.isAnyNamespace();
        if (mn.isAttribute()) {
            var attributes = this._attributes;
            if (attributes) {
                var newAttributes = this._attributes = [];
                for (var i = 0; i < attributes.length; i++) {
                    var node = attributes[i];
                    var attrName = node._name;
                    if ((anyName || attrName.name === localName) &&
                        (anyNamespace || attrName.matches(name))) {
                        node._parent = null;
                    }
                    else {
                        newAttributes.push(node);
                    }
                }
            }
        }
        else {
            if (this._children.some(function (v, i) {
                return (anyName || v._kind === 1 /* ASXMLKind.Element */ && v._name.name === name.name) &&
                    (anyNamespace || v._kind === 1 /* ASXMLKind.Element */ && v._name.matches(name));
            })) {
                return true;
            }
        }
    };
    ASXML.prototype.axHasProperty = function (mn) {
        if (this === this.axClass.dPrototype) {
            return _super.prototype.axHasPropertyInternal.call(this, mn);
        }
        return this.axHasPropertyInternal(mn);
    };
    ASXML.prototype.axHasPropertyInternal = function (mn) {
        if (this.hasProperty(mn)) {
            return true;
        }
        // HACK if child with specific name is not present, check object's attributes.
        // The presence of the attribute/method can be checked during with(), see #850.
        return !!this[this.axResolveMultiname(mn)];
    };
    ASXML.prototype.axDeleteProperty = function (mn) {
        if (this.deleteProperty(mn)) {
            return true;
        }
        // HACK if child with specific name is not present, check object's attributes.
        // The presence of the attribute/method can be checked during with(), see #850.
        return delete this[this.axResolveMultiname(mn)];
    };
    ASXML.prototype.axCallProperty = function (mn, args) {
        var method = this[this.axResolveMultiname(mn)];
        // The method might be dynamically defined on XML.prototype.
        if (!method) {
            method = this['$Bg' + mn.name];
        }
        // Check if the method exists before calling it.
        if (method) {
            validateCall(this.sec, method, args.length);
            return method.axApply(this, args);
        }
        // Otherwise, 11.2.2.1 CallMethod ( r , args )
        // If f == undefined and Type(base) is XMLList and base.[[Length]] == 1
        //   ii. Return the result of calling CallMethod(r0, args) recursively
        // f. If f == undefined and Type(base) is XML and base.hasSimpleContent () == true
        //   i. Let r0 be a new Reference with base object = ToObject(ToString(base)) and property
        // name = P ii. Return the result of calling CallMethod(r0, args) recursively
        if (this.hasSimpleContent()) {
            return this.sec.box(toString(this, this.sec)).axCallProperty(mn, args);
        }
        this.sec.throwError('TypeError', Errors.CallOfNonFunctionError, 'value');
    };
    ASXML.prototype._delete = function (key, isMethod) {
        release || release || notImplemented('XML.[[Delete]]');
    };
    ASXML.prototype.deleteByIndex = function (p) {
        var i = p >>> 0;
        if (String(i) !== String(p)) {
            throw 'TypeError in XML.prototype.deleteByIndex(): invalid index ' + p;
        }
        var children = this._children;
        if (p < children.length && children[p]) {
            children[p]._parent = null;
            children.splice(p, 1);
        }
    };
    // 9.1.1.11 [[Insert]] (P, V)
    ASXML.prototype.insert = function (p, v) {
        // Step 1.
        if (this._kind > 1 /* ASXMLKind.Element */) {
            return;
        }
        // Steps 2-3 (Guaranteed by assert).
        release || assert(typeof p === 'number' && isIndex(p));
        var i = p;
        // Step 4.
        if (v && v.axClass === this.axClass) {
            var a = this;
            while (a) {
                if (a === v) {
                    this.sec.throwError('TypeError', Errors.XMLIllegalCyclicalLoop);
                }
                a = a._parent;
            }
        }
        // Step 5.
        var n = 1;
        // Step 6.
        if (v && v.axClass === this.sec.AXXMLList) {
            n = v._children.length;
            // Step 7.
            if (n === 0) {
                return;
            }
        }
        // Step 8.
        var ownChildren = this._children;
        for (var j = ownChildren.length - 1; j >= i; j--) {
            ownChildren[j + n] = ownChildren[j];
            assert(ownChildren[0]);
        }
        // Step 9 (implicit).
        // Step 10.
        if (v && v.axClass === this.sec.AXXMLList) {
            n = v._children.length;
            for (var j = 0; j < n; j++) {
                v._children[j]._parent = this;
                ownChildren[i + j] = v._children[j];
            }
            // Step 11.
        }
        else {
            //x.replace(i, v), inlined;
            if (!(v && v.axClass === this.axClass)) {
                v = this.sec.AXXML.Create(v);
            }
            v._parent = this;
            if (!ownChildren) {
                this._children = ownChildren = [];
            }
            ownChildren[i] = v;
            assert(ownChildren[0]);
        }
    };
    // 9.1.1.13 [[AddInScopeNamespace]] ( N )
    ASXML.prototype.addInScopeNamespace = function (ns) {
        if (this._kind === 3 /* ASXMLKind.Text */ ||
            this._kind === 4 /* ASXMLKind.Comment */ ||
            this._kind === 5 /* ASXMLKind.ProcessingInstruction */ ||
            this._kind === 2 /* ASXMLKind.Attribute */) {
            return;
        }
        var prefix = ns.prefix;
        if (prefix !== undefined) {
            if (prefix === '' && this._name.uri === '') {
                return;
            }
            var match_1 = null;
            this._inScopeNamespaces.forEach(function (v, i) {
                if (v.prefix === prefix) {
                    match_1 = v;
                }
            });
            if (match_1 !== null && match_1.uri !== ns.uri) {
                this._inScopeNamespaces.forEach(function (v, i) {
                    if (v.prefix === match_1.prefix) {
                        this._inScopeNamespaces[i] = ns; // replace old with new
                    }
                });
            }
            if (this._name.prefix === prefix) {
                this._name.prefix = undefined;
            }
            this._attributes.forEach(function (v, i) {
                if (v._name.prefix === prefix) {
                    v._name.prefix = undefined;
                }
            });
        }
    };
    ASXML.prototype.descendantsInto = function (name, xl) {
        if (this._kind !== 1 /* ASXMLKind.Element */) {
            return xl;
        }
        var length = xl._children.length;
        var isAny = name.isAnyName();
        if (name.isAttribute()) {
            // Get attributes
            this._attributes.forEach(function (v, i) {
                if (isAny || v._name.matches(name)) {
                    xl._children[length++] = v;
                    assert(xl._children[0] != null);
                }
            });
        }
        else {
            // Get children
            this._children.forEach(function (v, i) {
                //@todo This is unstable, not all children has names
                if (!isAny && !v._name) {
                    return;
                    //debugger;
                }
                if (isAny || v._name.matches(name)) {
                    xl._children[length++] = v;
                    assert(xl._children[0] != null);
                }
            });
        }
        // Descend
        this._children.forEach(function (v, i) {
            v.descendantsInto(name, xl);
        });
        return xl;
    };
    ASXML.instanceConstructor = ASXML;
    ASXML._flags = ASXML_FLAGS.ALL;
    ASXML._prettyIndent = 2;
    return ASXML;
}(ASObject));
export { ASXML };
function createXML(sec, kind, uri, name, prefix) {
    if (kind === void 0) { kind = 3 /* ASXMLKind.Text */; }
    if (uri === void 0) { uri = ''; }
    if (name === void 0) { name = ''; }
    var xml = sec.AXXML.Create();
    var ns = internPrefixedNamespace(0 /* NamespaceType.Public */, uri, prefix || '');
    var mn = new Multiname(null, 0, kind === 2 /* ASXMLKind.Attribute */ ? 13 /* CONSTANT.QNameA */ : 7 /* CONSTANT.QName */, [ns], name, null);
    xml.init(kind, mn);
    return xml;
}
var ASXMLList = /** @class */ (function (_super) {
    __extends(ASXMLList, _super);
    function ASXMLList(value) {
        var _this = _super.call(this) || this;
        _this._children = [];
        if (isNullOrUndefined(value)) {
            value = '';
        }
        if (!value) {
            return _this;
        }
        if (value && value.axClass === _this.sec.AXXMLList) {
            var children = value._children;
            for (var i = 0; i < children.length; i++) {
                var child = children[i];
                _this._children[i] = child;
                assert(_this._children[0]);
            }
        }
        else {
            toXMLList(value, _this);
        }
        return _this;
    }
    ASXMLList.classInitializer = function () {
        defineNonEnumerableProperty(this, '$Bglength', 1);
        var proto = this.dPrototype;
        var asProto = ASXMLList.prototype;
        defineNonEnumerableProperty(proto, '$BgvalueOf', Object.prototype['$BgvalueOf']);
        defineNonEnumerableProperty(proto, '$BghasOwnProperty', asProto.native_hasOwnProperty);
        defineNonEnumerableProperty(proto, '$BgpropertyIsEnumerable', asProto.native_propertyIsEnumerable);
        addPrototypeFunctionAlias(proto, '$BgtoString', asProto.toString);
        addPrototypeFunctionAlias(proto, '$BgaddNamespace', asProto.addNamespace);
        addPrototypeFunctionAlias(proto, '$BgappendChild', asProto.appendChild);
        addPrototypeFunctionAlias(proto, '$Bgattribute', asProto.attribute);
        addPrototypeFunctionAlias(proto, '$Bgattributes', asProto.attributes);
        addPrototypeFunctionAlias(proto, '$Bgchild', asProto.child);
        addPrototypeFunctionAlias(proto, '$BgchildIndex', asProto.childIndex);
        addPrototypeFunctionAlias(proto, '$Bgchildren', asProto.children);
        addPrototypeFunctionAlias(proto, '$Bgcomments', asProto.comments);
        addPrototypeFunctionAlias(proto, '$Bgcontains', asProto.contains);
        addPrototypeFunctionAlias(proto, '$Bgcopy', asProto.copy);
        addPrototypeFunctionAlias(proto, '$Bgdescendants', asProto.descendants);
        addPrototypeFunctionAlias(proto, '$Bgelements', asProto.elements);
        addPrototypeFunctionAlias(proto, '$BghasComplexContent', asProto.hasComplexContent);
        addPrototypeFunctionAlias(proto, '$BghasSimpleContent', asProto.hasSimpleContent);
        addPrototypeFunctionAlias(proto, '$BginScopeNamespaces', asProto.inScopeNamespaces);
        addPrototypeFunctionAlias(proto, '$BginsertChildAfter', asProto.insertChildAfter);
        addPrototypeFunctionAlias(proto, '$BginsertChildBefore', asProto.insertChildBefore);
        addPrototypeFunctionAlias(proto, '$Bglength', asProto.length);
        addPrototypeFunctionAlias(proto, '$BglocalName', asProto.localName);
        addPrototypeFunctionAlias(proto, '$Bgname', asProto.name);
        addPrototypeFunctionAlias(proto, '$Bgnamespace', asProto.namespace);
        addPrototypeFunctionAlias(proto, '$BgnamespaceDeclarations', asProto.namespaceDeclarations);
        addPrototypeFunctionAlias(proto, '$BgnodeKind', asProto.nodeKind);
        addPrototypeFunctionAlias(proto, '$Bgnormalize', asProto.normalize);
        addPrototypeFunctionAlias(proto, '$Bgparent', asProto.parent);
        addPrototypeFunctionAlias(proto, '$BgprocessingInstructions', asProto.processingInstructions);
        addPrototypeFunctionAlias(proto, '$BgprependChild', asProto.prependChild);
        addPrototypeFunctionAlias(proto, '$BgremoveNamespace', asProto.removeNamespace);
        addPrototypeFunctionAlias(proto, '$Bgreplace', asProto.replace);
        addPrototypeFunctionAlias(proto, '$BgsetChildren', asProto.setChildren);
        addPrototypeFunctionAlias(proto, '$BgsetLocalName', asProto.setLocalName);
        addPrototypeFunctionAlias(proto, '$BgsetName', asProto.setName);
        addPrototypeFunctionAlias(proto, '$BgsetNamespace', asProto.setNamespace);
        addPrototypeFunctionAlias(proto, '$Bgtext', asProto.text);
        addPrototypeFunctionAlias(proto, '$BgtoXMLString', asProto.toXMLString);
        addPrototypeFunctionAlias(proto, '$BgtoJSON', asProto.toJSON);
    };
    ASXMLList.axApply = function (_self, args) {
        var value = args[0];
        // 13.5.1 The XMLList Constructor Called as a Function
        if (isNullOrUndefined(value)) {
            value = '';
        }
        if (value && value.axClass === this.sec.AXXMLList) {
            return value;
        }
        var list = this.sec.AXXMLList.Create();
        toXMLList(value, list);
        return list;
    };
    // 11.4.1 The Addition Operator ( + )
    ASXMLList.addXML = function (left, right) {
        var result;
        if (left.axClass === left.sec.AXXML) {
            result = left.sec.AXXMLList.Create();
            result.append(left);
        }
        else {
            result = left;
        }
        result.append(right);
        return result;
    };
    ASXMLList.Create = function (value) {
        var list = Object.create(this.sec.AXXMLList.tPrototype);
        list.axInitializer(value);
        return list;
    };
    ASXMLList.CreateList = function (targetObject, targetProperty) {
        var list = this.Create();
        list._targetObject = targetObject;
        list._targetProperty = targetProperty;
        return list;
    };
    ASXMLList.prototype.valueOf = function () {
        return this;
    };
    // E4X 11.5.1 The Abstract Equality Comparison Algorithm, steps 1-2.
    // (but really 9.2.1.9 [[Equals]] (V))
    ASXMLList.prototype.equals = function (other) {
        var children = this._children;
        // Step 1.
        if (other === undefined && children.length === 0) {
            return true;
        }
        // Step 2.
        if (other && other.axClass === this.sec.AXXMLList) {
            var otherChildren = other._children;
            // Step 2.a.
            if (otherChildren.length !== children.length) {
                return false;
            }
            // Step 2.b.
            for (var i = 0; i < children.length; i++) {
                if (!children[i].equals(otherChildren[i])) {
                    return false;
                }
            }
            // Step 2.c.
            return true;
        }
        // Steps 3-4.
        return children.length === 1 && children[0].equals(other);
    };
    ASXMLList.prototype.toString = function () {
        if (this.hasComplexContent()) {
            return this.toXMLString();
        }
        var s = '';
        for (var i = 0; i < this._children.length; i++) {
            s += toString(this._children[i], this.sec);
        }
        return s;
    };
    // 9.2.1.7 [[DeepCopy]] ( )
    ASXMLList.prototype._deepCopy = function () {
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        var length = this._children.length;
        for (var i = 0; i < length; i++) {
            xl._children[i] = this._children[i]._deepCopy();
            assert(xl._children[0]);
        }
        return xl;
    };
    ASXMLList.prototype._shallowCopy = function () {
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        var length = this._children.length;
        for (var i = 0; i < length; i++) {
            xl._children[i] = this._children[i];
            assert(xl._children[0]);
        }
        return xl;
    };
    // 13.5.4.12 XMLList.prototype.hasOwnProperty ( P )
    ASXMLList.prototype.native_hasOwnProperty = function (P) {
        P = axCoerceString(P);
        if (this === this.sec.AXXMLList.dPrototype) {
            return ASObject.prototype.native_hasOwnProperty.call(this, P);
        }
        if (isIndex(P)) {
            return (P | 0) < this._children.length;
        }
        var mn = toXMLName(P, this.sec);
        var children = this._children;
        for (var i = 0; i < children.length; i++) {
            var node = children[i];
            if (node._kind === 1 /* ASXMLKind.Element */) {
                if (node.hasProperty(mn)) {
                    return true;
                }
            }
        }
        return false;
    };
    // 13.5.4.19 XMLList.prototype.propertyIsEnumerable ( P )
    ASXMLList.prototype.native_propertyIsEnumerable = function (P) {
        return isIndex(P) && (P | 0) < this._children.length;
    };
    ASXMLList.prototype.attribute = function (arg) {
        if (isNullOrUndefined(arg) && arguments.length > 0) {
            this.sec.throwError('TypeError', Errors.ConvertUndefinedToObjectError);
        }
        if (arg && arg.axClass === this.sec.AXQName) {
            return this.getProperty(arg.name);
        }
        arg = axCoerceString(arg);
        if (arg === '*' || arguments.length === 0) {
            arg = null;
        }
        tmpMultiname.name = arg;
        tmpMultiname.namespaces = [Namespace.PUBLIC];
        tmpMultiname.kind = 13 /* CONSTANT.QNameA */;
        return this.getProperty(tmpMultiname);
    };
    ASXMLList.prototype.attributes = function () {
        // 13.5.4.3 XMLList.prototype.attributes ( )
        tmpMultiname.name = null;
        tmpMultiname.namespaces = [];
        tmpMultiname.kind = 13 /* CONSTANT.QNameA */;
        return this.getProperty(tmpMultiname);
    };
    ASXMLList.prototype.child = function (propertyName) {
        if (isIndex(propertyName)) {
            var list = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
            if ((propertyName | 0) < this._children.length) {
                list._children[0] = this._children[propertyName | 0]._deepCopy();
                assert(list._children[0]);
            }
            return list;
        }
        return this.getProperty(toXMLName(propertyName, this.sec));
    };
    ASXMLList.prototype.children = function () {
        // 13.5.4.4 XMLList.prototype.child ( propertyName )
        return this.getProperty(anyMultiname);
    };
    // 9.2.1.8 [[Descendants]] (P)
    ASXMLList.prototype.descendants = function (name_) {
        var name = toXMLName(name_, this.sec);
        var list = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        for (var i = 0; i < this._children.length; i++) {
            var child = this._children[i];
            if (child._kind === 1 /* ASXMLKind.Element */) {
                child.descendantsInto(name, list);
            }
        }
        return list;
    };
    ASXMLList.prototype.comments = function () {
        // 13.5.4.6 XMLList.prototype.comments ( )
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        this._children.forEach(function (child) {
            if (child._kind === 1 /* ASXMLKind.Element */) {
                var r = child.comments();
                Array.prototype.push.apply(xl._children, r._children);
            }
        });
        return xl;
    };
    // 13.5.4.8 XMLList.prototype.contains ( value )
    ASXMLList.prototype.contains = function (value) {
        var children = this._children;
        for (var i = 0; i < children.length; i++) {
            var child = children[i];
            if (child.equals(value)) {
                return true;
            }
        }
        return false;
    };
    ASXMLList.prototype.copy = function () {
        // 13.5.4.9 XMLList.prototype.copy ( )
        return this._deepCopy();
    };
    ASXMLList.prototype.elements = function (name) {
        // 13.5.4.11 XMLList.prototype.elements ( [ name ] )
        var mn = toXMLName(name, this.sec);
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, mn);
        this._children.forEach(function (child) {
            if (child._kind === 1 /* ASXMLKind.Element */) {
                var r = child.elements(mn);
                Array.prototype.push.apply(xl._children, r._children);
            }
        });
        return xl;
    };
    ASXMLList.prototype.hasComplexContent = function () {
        // 13.5.4.13 XMLList.prototype.hasComplexContent( )
        switch (this._children.length) {
            case 0:
                return false;
            case 1:
                return this._children[0].hasComplexContent();
            default:
                return this._children.some(function (child) {
                    return child._kind === 1 /* ASXMLKind.Element */;
                });
        }
    };
    ASXMLList.prototype.hasSimpleContent = function () {
        // 13.5.4.14 XMLList.prototype.hasSimpleContent( )
        switch (this._children.length) {
            case 0:
                return true;
            case 1:
                return this._children[0].hasSimpleContent();
            default:
                return this._children.every(function (child) {
                    return child._kind !== 1 /* ASXMLKind.Element */;
                });
        }
    };
    ASXMLList.prototype.length = function () {
        return this._children.length;
    };
    ASXMLList.prototype.name = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'name');
        }
        return this._children[0].name();
    };
    // 13.5.4.16 XMLList.prototype.normalize ( )
    ASXMLList.prototype.normalize = function () {
        // Steps 1-2.
        for (var i = 0; i < this._children.length;) {
            var child = this._children[i];
            // Step 2.a.
            if (child._kind === 1 /* ASXMLKind.Element */) {
                child.normalize();
                i++;
                // Step 2.b.
            }
            else if (child._kind === 3 /* ASXMLKind.Text */) {
                // Step 2.b.i.
                for (i++; i < this._children.length;) {
                    var nextChild = this._children[i];
                    if (nextChild._kind !== 3 /* ASXMLKind.Text */) {
                        break;
                    }
                    child._value += nextChild._value;
                    this.removeByIndex(i);
                }
                // Step 2.b.ii.
                if (child._value.length === 0) {
                    this.removeByIndex(i);
                }
                else { // Step 2.b.iii.
                    i++;
                }
            }
            else { // Step 2.c.
                i++;
            }
        }
        return this;
    };
    ASXMLList.prototype.parent = function () {
        // 13.5.4.17 XMLList.prototype.parent ( )
        var children = this._children;
        if (children.length === 0) {
            return undefined;
        }
        var parent = children[0]._parent;
        for (var i = 1; i < children.length; i++) {
            if (children[i]._parent !== parent) {
                return undefined;
            }
        }
        return parent;
    };
    // 13.5.4.18 XMLList.prototype.processingInstructions ( [ name ] )
    ASXMLList.prototype.processingInstructions = function (name_) {
        // (Numbering in the spec starts at 6.)
        // Step 6 (implicit).
        // Step 7.
        var name = toXMLName(name_, this.sec);
        // Step 8.
        var list = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        list._targetObject = this;
        list._targetProperty = null;
        // Step 9.
        var children = this._children;
        for (var i = 0; i < children.length; i++) {
            children[i].processingInstructionsInto(name, list);
        }
        // Step 10.
        return list;
    };
    ASXMLList.prototype.text = function () {
        // 13.5.4.20 XMLList.prototype.text ( )
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, this._targetProperty);
        this._children.forEach(function (v, i) {
            if (v._kind === 1 /* ASXMLKind.Element */) {
                var gq = v.text();
                if (gq._children.length > 0) {
                    xl._children.push(gq);
                }
            }
        });
        return xl;
    };
    ASXMLList.prototype.toXMLString = function () {
        // 10.2.2 ToXMLString Applied to the XMLList Type
        var sec = this.sec;
        return this._children.map(function (childNode) {
            return toXMLString(sec, childNode);
        }).join(sec.AXXML.prettyPrinting ? '\n' : '');
    };
    ASXMLList.prototype.toJSON = function (k) {
        return 'XMLList';
    };
    ASXMLList.prototype.addNamespace = function (ns) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'addNamespace');
        }
        var xml = this._children[0];
        xml.addNamespace(ns);
        return xml;
    };
    ASXMLList.prototype.appendChild = function (child) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'appendChild');
        }
        var xml = this._children[0];
        xml.appendChild(child);
        return xml;
    };
    // 9.2.1.6 [[append]] (V)
    ASXMLList.prototype.append = function (V) {
        // Step 1.
        var children = this._children;
        var i = children.length;
        // Step 2.
        var n = 1;
        // Step 3.
        if (V && V.axClass === this.sec.AXXMLList) {
            this._targetObject = V._targetObject;
            this._targetProperty = V._targetProperty;
            var valueChildren = V._children;
            n = valueChildren.length;
            if (n === 0) {
                return;
            }
            for (var j = 0; j < valueChildren.length; j++) {
                children[i + j] = valueChildren[j];
            }
            return;
        }
        release || assert(V.axClass === this.sec.AXXML);
        // Step 4.
        children[i] = V;
        // Step 5 (implicit).
    };
    ASXMLList.prototype.childIndex = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'childIndex');
        }
        return this._children[0].childIndex();
    };
    ASXMLList.prototype.inScopeNamespaces = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'inScopeNamespaces');
        }
        return this._children[0].inScopeNamespaces();
    };
    ASXMLList.prototype.insertChildAfter = function (child1, child2) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'insertChildAfter');
        }
        return this._children[0].insertChildAfter(child1, child2);
    };
    ASXMLList.prototype.insertChildBefore = function (child1, child2) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'insertChildBefore');
        }
        return this._children[0].insertChildBefore(child1, child2);
    };
    ASXMLList.prototype.nodeKind = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'nodeKind');
        }
        return this._children[0].nodeKind();
    };
    ASXMLList.prototype.namespace = function (prefix) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'namespace');
        }
        var firstChild = this._children[0];
        return arguments.length ? firstChild.namespace(prefix) : firstChild.namespace();
    };
    ASXMLList.prototype.localName = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'localName');
        }
        return this._children[0].localName();
    };
    ASXMLList.prototype.namespaceDeclarations = function () {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'namespaceDeclarations');
        }
        return this._children[0].namespaceDeclarations();
    };
    ASXMLList.prototype.prependChild = function (value) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'prependChild');
        }
        return this._children[0].prependChild(value);
    };
    ASXMLList.prototype.removeNamespace = function (ns) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'removeNamespace');
        }
        return this._children[0].removeNamespace(ns);
    };
    ASXMLList.prototype.replace = function (propertyName, value) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'replace');
        }
        return this._children[0].replace(propertyName, value);
    };
    ASXMLList.prototype.setChildren = function (value) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'setChildren');
        }
        return this._children[0].setChildren(value);
    };
    ASXMLList.prototype.setLocalName = function (name) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'setLocalName');
        }
        return this._children[0].setLocalName(name);
    };
    ASXMLList.prototype.setName = function (name) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'setName');
        }
        return this._children[0].setName(name);
    };
    ASXMLList.prototype.setNamespace = function (ns) {
        if (this._children.length !== 1) {
            this.sec.throwError('TypeError', Errors.XMLOnlyWorksWithOneItemLists, 'setNamespace');
        }
        return this._children[0].setNamespace(ns);
    };
    ASXMLList.prototype.axGetEnumerableKeys = function () {
        if (this === this.axClass.dPrototype) {
            return _super.prototype.axGetEnumerableKeys.call(this);
        }
        return Object.keys(this._children);
    };
    // 9.2.1.1 [[Get]] (P)
    ASXMLList.prototype.getProperty = function (mn) {
        var nm = mn.name;
        if (isIndex(nm)) {
            return this._children[nm | 0];
        }
        var isAnyName = nm === null || nm === '*';
        var isAnyNamespace = mn.isAnyNamespace();
        var isAttribute = mn.isAttribute();
        var xl = this.sec.AXXMLList.CreateList(this._targetObject, mn);
        var children = this._children;
        for (var i = 0; i < children.length; i++) {
            var v = children[i];
            if (v._kind === 1 /* ASXMLKind.Element */) {
                // i. Let gq be the result of calling the [[Get]] method of x[i] with argument P
                // We do this inline instead to reduce the amount of temporarily created XMLLists.
                if (isAttribute) {
                    var attributes = v._attributes;
                    for (var j = 0; attributes && j < attributes.length; j++) {
                        var v_1 = attributes[j];
                        if ((isAnyName || v_1._name.name === nm) &&
                            (isAnyNamespace || v_1._name.matches(mn))) {
                            xl._children.push(v_1);
                        }
                    }
                }
                else {
                    var descendants = v._children;
                    for (var j = 0; descendants && j < descendants.length; j++) {
                        var v_2 = descendants[j];
                        if ((isAnyName || v_2._kind === 1 /* ASXMLKind.Element */ && v_2._name.name === nm) &&
                            (isAnyNamespace || v_2._name.matches(mn))) {
                            xl._children.push(v_2);
                        }
                    }
                }
            }
        }
        return xl;
    };
    ASXMLList.prototype.axGetProperty = function (mn) {
        if (this === this.axClass.dPrototype) {
            var value = this[this.axResolveMultiname(mn)];
            release || checkValue(value);
            return value;
        }
        return this.getProperty(coerceE4XMultiname(mn, this.sec));
    };
    ASXMLList.prototype.axGetPublicProperty = function (nm) {
        if (this === this.axClass.dPrototype) {
            var value = this[Multiname.getPublicMangledName(nm)];
            release || checkValue(value);
            return value;
        }
        if (isIndex(nm)) {
            return this._children[nm | 0];
        }
        tmpMultiname.name = nm;
        tmpMultiname.namespaces = [Namespace.PUBLIC];
        tmpMultiname.kind = 7 /* CONSTANT.QName */;
        return this.getProperty(tmpMultiname);
    };
    ASXMLList.prototype.hasProperty = function (mn) {
        if (isIndex(mn.name)) {
            return Number(mn.name) < this._children.length;
        }
        // TODO scan children on property presence?
        return true;
    };
    ASXMLList.prototype.axHasProperty = function (mn) {
        return this.hasProperty(mn);
    };
    ASXMLList.prototype.axHasPropertyInternal = function (mn) {
        return this.hasProperty(mn);
    };
    // 9.1.1.10 [[ResolveValue]] ( )
    ASXMLList.prototype.resolveValue = function () {
        return this;
    };
    // 9.2.1.2 [[Put]] (P, V)
    ASXMLList.prototype.setProperty = function (mn, value) {
        // Steps 1-2.
        if (isIndex(mn.name)) {
            var i = mn.name | 0;
            // Step 2.b.
            var r = null;
            // Step 2.a.
            if (this._targetObject) {
                r = this._targetObject.resolveValue();
                if (r === null) {
                    return;
                }
            }
            // Step 2.c.
            var length_1 = this._children.length;
            if (i >= length_1) {
                // Step 2.c.i.
                if (r && r.axClass === this.sec.AXXMLList) {
                    // Step 2.c.i.1.
                    if (r._children.length !== 1) {
                        return;
                    }
                    // Step 2.c.i.2.
                    r = r._children[0];
                }
                release || assert(r === null || r.axClass === this.sec.AXXML);
                // Step 2.c.ii.
                if (r && r._kind !== 1 /* ASXMLKind.Element */) {
                    return;
                }
                // Step 2.c.iii.
                var y = this.sec.AXXML.Create();
                y._parent = r;
                var yName = this._targetProperty;
                var yKind = 3 /* ASXMLKind.Text */;
                // Step 2.c.iv.
                if (this._targetProperty && this._targetProperty.isAttribute()) {
                    if (r.hasProperty(this._targetProperty)) {
                        return;
                    }
                    yKind = 2 /* ASXMLKind.Attribute */;
                    // Step 2.c.v.
                }
                else if (!this._targetProperty || this._targetProperty.name === null) {
                    yName = null;
                    yKind = 3 /* ASXMLKind.Text */;
                    // Step 2.c.vi.
                }
                else {
                    yKind = 1 /* ASXMLKind.Element */;
                }
                y.init(yKind, yName);
                // Step 2.c.vii.
                i = length_1;
                // Step 2.c.viii.
                if (y._kind !== 2 /* ASXMLKind.Attribute */) {
                    // Step 2.c.viii.1.
                    if (r !== null) {
                        var j = void 0;
                        // Step 2.c.viii.1.a.
                        if (i > 0) {
                            var lastChild = this._children[i - 1];
                            var rLength = r._children.length - 1;
                            for (j = 0; j < rLength; j++) {
                                if (r._children[j] === lastChild) {
                                    release || assert(r._children[0]);
                                    break;
                                }
                            }
                            // Step 2.c.viii.1.b.
                        }
                        else {
                            j = r._children.length - 1;
                        }
                        // Step 2.c.viii.1.c.
                        r._children[j + 1] = y;
                        assert(r._children[0]);
                        y._parent = r;
                    }
                    // Step 2.c.viii.2.
                    if (value && value.axClass === this.sec.AXXML) {
                        y._name = value._name;
                        // Step 2.c.viii.3
                    }
                    else if (value && value.axClass === this.sec.AXXMLList) {
                        y._name = value._targetProperty;
                    }
                    // Step 2.c.ix.
                    this.append(y);
                }
            }
            // Step 2.d.
            if (!isXMLType(value, this.sec) ||
                value._kind === 3 /* ASXMLKind.Text */ || value._kind === 2 /* ASXMLKind.Attribute */) {
                value = value + '';
            }
            var currentChild = this._children[i];
            var childKind = currentChild._kind;
            var parent_1 = currentChild._parent;
            // Step 2.e.
            if (childKind === 2 /* ASXMLKind.Attribute */) {
                var indexInParent = parent_1._children.indexOf(currentChild);
                parent_1.setProperty(currentChild._name, false);
                this._children[i] = parent_1._children[indexInParent];
                assert(this._children[0]);
                return;
            }
            // Step 2.f.
            if (value && value.axClass === this.sec.AXXMLList) {
                // Step 2.f.i.
                var c = value._shallowCopy();
                var cLength = c._children.length;
                // Step 2.f.ii. (implemented above.)
                // Step 2.f.iii.
                if (parent_1 !== null) {
                    // Step 2.f.iii.1.
                    var q = parent_1._children.indexOf(currentChild);
                    // Step 2.f.iii.2.
                    parent_1._replaceByIndex(q, c);
                    // Step 2.f.iii.3.
                    for (var j = 0; j < cLength; j++) {
                        c._children[j] = parent_1._children[q + j];
                    }
                }
                // Step 2.f.iv.
                if (cLength === 0) {
                    for (var j = i + 1; j < length_1; j++) {
                        this._children[j - 1] = this._children[j];
                        assert(this._children[0]);
                    }
                    // Step 2.f.vii. (only required if we're shrinking the XMLList).
                    this._children.length--;
                    // Step 2.f.v.
                }
                else {
                    for (var j = length_1 - 1; j > i; j--) {
                        this._children[j + cLength - 1] = this._children[j];
                        assert(this._children[0]);
                    }
                }
                // Step 2.f.vi.
                for (var j = 0; j < cLength; j++) {
                    this._children[i + j] = c._children[j];
                    assert(this._children[0]);
                }
                return;
            }
            // Step 2.g.
            if (childKind >= 3 /* ASXMLKind.Text */ || value && value.axClass === this.sec.AXXML) {
                // Step 2.g.i. (implemented above.)
                // Step 2.g.ii.
                if (parent_1 !== null) {
                    // Step 2.g.ii.1.
                    var q = parent_1._children.indexOf(currentChild);
                    // Step 2.g.ii.2.
                    parent_1._replaceByIndex(q, value);
                    // Step 2.g.ii.3.
                    value = parent_1._children[q];
                }
                // Step 2.g.iii.
                if (typeof value === 'string') {
                    var t = this.sec.AXXML.Create(value);
                    this._children[i] = t;
                    assert(this._children[0]);
                    // Step 2.g.iv.
                }
                else {
                    release || assert(this.sec.AXXML.axIsType(value));
                    this._children[i] = value;
                    assert(this._children[0]);
                }
                return;
            }
            // Step 2.h.
            currentChild.setProperty(anyMultiname, value);
            return;
        }
        // Step 3.
        if (this._children.length === 0) {
            // Step 3.a.i.
            var r = this.resolveValue();
            // Step 3.a.ii.
            if (r === null || r._children.length !== 1) {
                return;
            }
            // Step 3.a.iii.
            this.append(r._children[0]);
        }
        // Step 3.b.
        if (this._children.length === 1) {
            this._children[0].setProperty(mn, value);
            // Step 4.
            return;
        }
        // Not in the spec, but in Flash.
        this.sec.throwError('TypeError', Errors.XMLAssigmentOneItemLists);
    };
    ASXMLList.prototype.axSetProperty = function (mn, value, _bc) {
        if (this === this.axClass.dPrototype) {
            release || checkValue(value);
            this[this.axResolveMultiname(mn)] = value;
            return;
        }
        this.setProperty(coerceE4XMultiname(mn, this.sec), value);
    };
    // 9.2.1.3 [[Delete]] (P)
    ASXMLList.prototype.axDeleteProperty = function (mn) {
        var name = mn.name;
        // Steps 1-2.
        if (isIndex(name)) {
            var i = name | 0;
            // Step 2.a.
            if (i >= this._children.length) {
                return true;
            }
            // Step 2.b.
            this.removeByIndex(i);
            return true;
        }
        // Step 3.
        for (var i = 0; i < this._children.length; i++) {
            var child = this._children[i];
            if (child._kind === 1 /* ASXMLKind.Element */) {
                child.deleteProperty(mn);
            }
        }
        // Step 4.
        return true;
    };
    ASXMLList.prototype.removeByIndex = function (index) {
        var child = this._children[index];
        var parent = child._parent;
        if (parent) {
            child._parent = null;
            parent._children.splice(parent._children.indexOf(child), 1);
        }
        this._children.splice(index, 1);
    };
    ASXMLList.prototype.axCallProperty = function (mn, args) {
        var method = this[this.axResolveMultiname(mn)];
        // Check if the method exists before calling it.
        if (method) {
            validateCall(this.sec, method, args.length);
            return method.axApply(this, args);
        }
        // Otherwise, 11.2.2.1 CallMethod ( r , args )
        // If f == undefined and Type(base) is XMLList and base.[[Length]] == 1
        //   ii. Return the result of calling CallMethod(r0, args) recursively
        if (this._children.length === 1) {
            return this._children[0].axCallProperty(mn, args);
        }
        this.sec.throwError('TypeError', Errors.CallOfNonFunctionError, 'value');
    };
    ASXMLList.instanceConstructor = ASXMLList;
    return ASXMLList;
}(ASObject));
export { ASXMLList };
